import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Shield, ShieldOff, Trash2, Check, X as XIcon, Pause, Award } from "lucide-react";

const ROLES = [
  { value: "", label: "—" },
  { value: "entrepreneur", label: "Entrepreneur" },
  { value: "business_owner", label: "Business Owner" },
  { value: "investor", label: "Investor" },
  { value: "contractor", label: "Contractor" },
  { value: "influencer", label: "Influencer" },
];

const STATUS_CHIP = {
  available: "bg-emerald-50 text-emerald-700 border-emerald-200",
  under_review: "bg-amber-50 text-amber-800 border-amber-200",
  assigned: "bg-red-50 text-red-700 border-red-200",
  reserved: "bg-zinc-100 text-zinc-700 border-zinc-300",
};

function Stat({ label, value, testid }) {
  return (
    <div className="border border-zinc-200 p-5 bg-white" data-testid={testid}>
      <div className="overline text-zinc-500">{label}</div>
      <div className="font-display font-black text-3xl mt-2">{value}</div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="overline text-zinc-500">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-2 w-full border border-zinc-300 px-3 py-2 outline-none focus:border-[#C8102E] text-sm"
      />
    </div>
  );
}

export default function Admin() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [settings, setSettings] = useState({
    whatsapp_url: "", telegram_url: "", tagline: "",
    hero_audience_badge: "", hero_tag: "",
    hero_headline_1: "", hero_headline_2: "", hero_headline_3: "", hero_sub: "",
    hero_cta_earn: "", hero_cta_explore: "", hero_cta_list: "",
    phone_stat_opps: "", phone_stat_vendors: "",
    whatsapp_number: "", bank_name: "", bank_account_name: "", bank_account_number: "",
    payment_instructions: "", invoice_prefix: "", receipt_message: "",
    commission_affiliate_pct: "", commission_agent_pct: "",
    commission_stockist_pct: "", commission_vendor_pct: "",
    gateway_provider: "", gateway_merchant_id: "", gateway_api_key: "",
    gateway_secret_key: "", gateway_callback_url: "", gateway_return_url: "", gateway_webhook_url: "",
    wa_tpl_make_money: "", wa_tpl_promote_business: "", wa_tpl_join_artist: "",
    wa_tpl_join_affiliate: "", wa_tpl_join_stockist: "", wa_tpl_book_campaign: "",
    wa_tpl_contact: "", wa_tpl_payment_confirm: "",
  });
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [metrics, setMetrics] = useState(null);

  const load = async () => {
    const [{ data: s }, { data: u }, { data: t }, { data: cfg }, { data: m }] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/users"),
      api.get("/territories", { params: { scope: "all" } }),
      api.get("/admin/settings"),
      api.get("/admin/metrics"),
    ]);
    setStats(s);
    setUsers(u);
    setTerritories(t);
    setSettings(cfg);
    setMetrics(m);
  };

  useEffect(() => { load(); }, []);

  const updateUser = async (id, patch) => {
    try {
      await api.put(`/admin/users/${id}`, patch);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success("User deleted");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const setTerritoryStatus = async (t, status) => {
    try {
      const payload = { status };
      if (status === "assigned") {
        payload.assigned_to = t.applicant_id;
      }
      await api.put(`/admin/territories/${t.territory_id}`, payload);
      toast.success(`Territory marked ${status}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div>
        <div className="tag"><span className="w-1.5 h-1.5 bg-[#94959A]" /> Administration</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-3">Admin dashboard</h1>
        <p className="text-zinc-600 text-sm mt-1">Inspect the network, manage users and review territory requests.</p>
      </div>

      <div className="flex items-center gap-2 border-b border-zinc-200 overflow-x-auto">
        {[
          ["overview", "Overview"],
          ["leads", "Leads"],
          ["journeys", "Journeys"],
          ["commissions", "Commissions"],
          ["badges", "Badges"],
          ["stories", "Stories"],
          ["ads", "Ad Packages"],
          ["gateways", "Gateways"],
          ["analytics", "Analytics"],
          ["users", "Users"],
          ["territories", "Territories"],
          ["spotlights", "Spotlights"],
          ["revenue", "Revenue"],
          ["artists", "Artists"],
          ["pathways", "Pathways"],
          ["categories", "Categories"],
          ["opportunities-cms", "Opportunities"],
          ["settings", "Settings"],
        ].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`admin-tab-${k}`}
            className={`px-4 py-2 text-xs uppercase tracking-widest font-semibold border-b-2 whitespace-nowrap ${
              tab === k ? "border-[#C8102E] text-[#C8102E]" : "border-transparent hover:text-[#C8102E]"
            }`}
          >{l}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-growth-metrics">
            <Stat label="New members (7d)" value={stats?.new_members_7d ?? "—"} testid="admin-metric-new-members" />
            <Stat label="New applications (7d)" value={stats?.new_applications_7d ?? "—"} testid="admin-metric-new-applications" />
            <Stat label="Active opportunities" value={stats?.active_opportunities ?? "—"} testid="admin-metric-active-opportunities" />
            <Stat label="Territory requests (pending)" value={stats?.territory_requests_pending ?? "—"} testid="admin-metric-territory-requests" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="Total users" value={stats?.users ?? "—"} testid="admin-stat-users" />
            <Stat label="Opportunities" value={stats?.opportunities ?? "—"} testid="admin-stat-opportunities" />
            <Stat label="Applications" value={stats?.applications ?? "—"} testid="admin-stat-applications" />
            <Stat label="Territories" value={stats?.territories ?? "—"} testid="admin-stat-territories" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 border border-zinc-200">
            {Object.entries(stats?.by_role || {}).map(([role, count], i, arr) => (
              <div key={role} className={`p-5 ${i < arr.length - 1 ? "sm:border-r border-zinc-200" : ""}`} data-testid={`role-count-${role}`}>
                <div className="overline text-zinc-500">{role.replace("_", " ")}</div>
                <div className="font-display font-black text-2xl mt-2">{count}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "users" && (
        <div className="border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead>
              <tr className="text-left text-zinc-500 overline border-b border-zinc-200">
                <th className="p-3">Name</th>
                <th className="p-3">Email</th>
                <th className="p-3">Role</th>
                <th className="p-3">Verified</th>
                <th className="p-3">Admin</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`admin-user-${u.user_id}`}>
                  <td className="p-3 font-medium flex items-center gap-2">
                    {u.picture && <img src={u.picture} alt="" className="w-6 h-6 object-cover" />}
                    {u.name || "—"}
                  </td>
                  <td className="p-3 text-zinc-700">{u.email}</td>
                  <td className="p-3">
                    <select
                      value={u.role || ""}
                      onChange={(e) => updateUser(u.user_id, { role: e.target.value || null })}
                      data-testid={`admin-role-${u.user_id}`}
                      className="text-xs uppercase tracking-widest font-semibold border px-2 py-1 bg-white"
                    >
                      {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </td>
                  <td className="p-3">
                    <button
                      onClick={() => updateUser(u.user_id, { is_verified: !u.is_verified })}
                      data-testid={`admin-toggle-verified-${u.user_id}`}
                      className={`px-2 py-1 text-xs font-semibold uppercase tracking-widest border ${
                        u.is_verified ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "border-zinc-300 hover:bg-zinc-100"
                      }`}
                    >
                      {u.is_verified ? "Verified ✓" : "Verify"}
                    </button>
                  </td>
                  <td className="p-3">
                    <button
                      onClick={() => updateUser(u.user_id, { is_admin: !u.is_admin })}
                      data-testid={`admin-toggle-admin-${u.user_id}`}
                      className={`px-2 py-1 text-xs font-semibold uppercase tracking-widest border ${
                        u.is_admin ? "bg-[#C8102E] text-white border-[#C8102E]" : "border-zinc-300 hover:bg-zinc-100"
                      }`}
                    >
                      {u.is_admin ? <Shield className="w-3.5 h-3.5 inline" /> : <ShieldOff className="w-3.5 h-3.5 inline" />}
                    </button>
                  </td>
                  <td className="p-3 text-right">
                    <button onClick={() => removeUser(u)} className="px-2 py-1 hover:bg-red-50 text-red-600 text-xs inline-flex items-center gap-1" data-testid={`admin-delete-${u.user_id}`}>
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "territories" && (
        <div className="border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-territories-table">
            <thead>
              <tr className="text-left text-zinc-500 overline border-b border-zinc-200">
                <th className="p-3">Location</th>
                <th className="p-3">Applicant</th>
                <th className="p-3">Status</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {territories.length === 0 && (
                <tr><td colSpan={4} className="p-8 text-center text-zinc-500">No territory applications yet.</td></tr>
              )}
              {territories.map((t) => (
                <tr key={t.territory_id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`admin-territory-${t.territory_id}`}>
                  <td className="p-3">
                    <div className="font-medium">{t.city || t.district || t.state || t.country}</div>
                    <div className="text-xs text-zinc-500">{[t.country, t.state, t.district].filter(Boolean).join(" › ")}</div>
                  </td>
                  <td className="p-3 text-zinc-700">{t.applicant_name}</td>
                  <td className="p-3">
                    <span className={`text-[10px] uppercase tracking-widest font-semibold border px-2 py-0.5 ${STATUS_CHIP[t.status]}`}>
                      {t.status.replace("_", " ")}
                    </span>
                  </td>
                  <td className="p-3 text-right space-x-1">
                    <button onClick={() => setTerritoryStatus(t, "assigned")} data-testid={`admin-territory-assign-${t.territory_id}`} className="px-2 py-1 hover:bg-emerald-50 text-emerald-700 text-xs font-semibold inline-flex items-center gap-1">
                      <Check className="w-3.5 h-3.5" /> Assign
                    </button>
                    <button onClick={() => setTerritoryStatus(t, "reserved")} data-testid={`admin-territory-reserve-${t.territory_id}`} className="px-2 py-1 hover:bg-zinc-100 text-zinc-700 text-xs font-semibold inline-flex items-center gap-1">
                      <Pause className="w-3.5 h-3.5" /> Reserve
                    </button>
                    <button onClick={() => setTerritoryStatus(t, "available")} data-testid={`admin-territory-release-${t.territory_id}`} className="px-2 py-1 hover:bg-blue-50 text-[#C8102E] text-xs font-semibold inline-flex items-center gap-1">
                      <Award className="w-3.5 h-3.5" /> Release
                    </button>
                    <button onClick={() => setTerritoryStatus(t, "under_review")} data-testid={`admin-territory-review-${t.territory_id}`} className="px-2 py-1 hover:bg-amber-50 text-amber-700 text-xs font-semibold inline-flex items-center gap-1">
                      <XIcon className="w-3.5 h-3.5" /> Reset
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "analytics" && (
        <div className="space-y-6" data-testid="admin-analytics-panel">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="overline text-zinc-500">Launch funnel</div>
              <h3 className="font-display font-black text-xl tracking-tight">User acquisition & conversion</h3>
              <p className="text-xs text-zinc-500 mt-1">Internal counters — no third-party trackers.</p>
            </div>
            <button
              onClick={load}
              data-testid="admin-analytics-refresh"
              className="px-3 py-1.5 text-xs uppercase tracking-widest font-semibold border border-zinc-300 hover:bg-zinc-100"
            >
              Refresh
            </button>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-analytics-grid">
            {[
              { key: "homepage_visit",              label: "Homepage Visits" },
              { key: "role_selection",              label: "Role Selection Clicks" },
              { key: "registration_complete",       label: "Registration Completions" },
              { key: "opportunity_application",     label: "Opportunity Applications" },
              { key: "business_owner_registration", label: "Business Owner Registrations" },
              { key: "territory_application",       label: "Territory Applications" },
            ].map((m) => (
              <div key={m.key} data-testid={`metric-${m.key}`} className="border border-zinc-200 p-5 bg-white">
                <div className="overline text-zinc-500">{m.label}</div>
                <div className="font-display font-black text-4xl tracking-tight mt-2">
                  {(metrics?.counters?.[m.key] ?? 0).toLocaleString()}
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="admin-conversion-grid">
            {[
              { key: "role_select_rate",         label: "Visit → Role select",     suffix: "%" },
              { key: "registration_rate",        label: "Role select → Signup",    suffix: "%" },
              { key: "applications_per_signup",  label: "Applications / signup",   suffix: "%" },
            ].map((r) => (
              <div key={r.key} data-testid={`conversion-${r.key}`} className="border border-zinc-200 p-5 bg-white">
                <div className="overline text-zinc-500">{r.label}</div>
                <div className="font-display font-black text-3xl tracking-tight mt-2">
                  {metrics?.conversion?.[r.key] ?? 0}{r.suffix}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "settings" && (
        <div className="space-y-6 max-w-3xl" data-testid="admin-settings-panel">
          <div className="border border-zinc-200 bg-white p-6">
            <div className="overline text-zinc-500">Site content · Hero section</div>
            <h3 className="font-display font-black text-xl tracking-tight mt-1">Edit your landing page copy</h3>
            <p className="text-xs text-zinc-500 mt-2">
              These fields override the default landing copy. Leave blank to use the built-in defaults.
              Changes go live instantly — no developer needed.
            </p>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field testid="settings-hero-audience" label="Audience badge"
                value={settings.hero_audience_badge}
                placeholder="For Malaysians who want side income…"
                onChange={(v) => setSettings({ ...settings, hero_audience_badge: v })}
              />
              <Field testid="settings-hero-tag" label="Section tag"
                value={settings.hero_tag}
                placeholder="MALAYSIA'S ENTREPRENEUR GROWTH PLATFORM"
                onChange={(v) => setSettings({ ...settings, hero_tag: v })}
              />
              <Field testid="settings-hero-h1" label="Headline · line 1 (red)"
                value={settings.hero_headline_1}
                placeholder="Make Money Today."
                onChange={(v) => setSettings({ ...settings, hero_headline_1: v })}
              />
              <Field testid="settings-hero-h2" label="Headline · line 2"
                value={settings.hero_headline_2}
                placeholder="Build A Business"
                onChange={(v) => setSettings({ ...settings, hero_headline_2: v })}
              />
              <Field testid="settings-hero-h3" label="Headline · line 3"
                value={settings.hero_headline_3}
                placeholder="Tomorrow."
                onChange={(v) => setSettings({ ...settings, hero_headline_3: v })}
              />
              <div className="sm:col-span-2">
                <label className="overline text-zinc-500">Sub-headline (paragraph)</label>
                <textarea
                  value={settings.hero_sub}
                  onChange={(e) => setSettings({ ...settings, hero_sub: e.target.value })}
                  data-testid="settings-hero-sub"
                  rows={3}
                  placeholder="Discover verified income opportunities…"
                  className="mt-2 w-full border border-zinc-300 px-3 py-2 outline-none focus:border-[#C8102E] resize-none text-sm"
                />
              </div>
              <Field testid="settings-cta-earn"    label="CTA · Earn button"
                value={settings.hero_cta_earn}    placeholder="Explore Income Opportunities"
                onChange={(v) => setSettings({ ...settings, hero_cta_earn: v })} />
              <Field testid="settings-cta-explore" label="CTA · Explore button"
                value={settings.hero_cta_explore} placeholder="Start a Business"
                onChange={(v) => setSettings({ ...settings, hero_cta_explore: v })} />
              <Field testid="settings-cta-list"    label="CTA · List button"
                value={settings.hero_cta_list}    placeholder="Grow My Business"
                onChange={(v) => setSettings({ ...settings, hero_cta_list: v })} />
              <Field testid="settings-phone-opps" label="Phone mockup · Opportunities count"
                value={settings.phone_stat_opps}  placeholder="248"
                onChange={(v) => setSettings({ ...settings, phone_stat_opps: v })} />
              <Field testid="settings-phone-vendors" label="Phone mockup · Vendors count"
                value={settings.phone_stat_vendors} placeholder="17"
                onChange={(v) => setSettings({ ...settings, phone_stat_vendors: v })} />
            </div>
          </div>

          <div className="border border-zinc-200 bg-white p-6">
            <div className="overline text-zinc-500">Community & branding</div>
            <h3 className="font-display font-black text-xl tracking-tight mt-1">Footer & community</h3>
            <div className="mt-6 space-y-4">
              <div>
                <label className="overline text-zinc-500">Tagline</label>
                <input
                  value={settings.tagline}
                  onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
                  data-testid="admin-settings-tagline"
                  className="mt-2 w-full border border-zinc-300 px-3 py-2 outline-none focus:border-[#C8102E]"
                />
              </div>
              <div>
                <label className="overline text-zinc-500">WhatsApp community URL</label>
                <input
                  value={settings.whatsapp_url}
                  onChange={(e) => setSettings({ ...settings, whatsapp_url: e.target.value })}
                  data-testid="admin-settings-whatsapp"
                  placeholder="https://chat.whatsapp.com/..."
                  className="mt-2 w-full border border-zinc-300 px-3 py-2 outline-none focus:border-[#C8102E]"
                />
              </div>
              <div>
                <label className="overline text-zinc-500">Telegram community URL</label>
                <input
                  value={settings.telegram_url}
                  onChange={(e) => setSettings({ ...settings, telegram_url: e.target.value })}
                  data-testid="admin-settings-telegram"
                  placeholder="https://t.me/..."
                  className="mt-2 w-full border border-zinc-300 px-3 py-2 outline-none focus:border-[#C8102E]"
                />
              </div>
            </div>
          </div>

          <div className="border border-zinc-200 bg-white p-6">
            <div className="overline text-zinc-500">Payments · Manual bank transfer</div>
            <h3 className="font-display font-black text-xl tracking-tight mt-1">Bank details shown to buyers</h3>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field testid="settings-wa-number"  label="Default WhatsApp number (with country code)" value={settings.whatsapp_number} placeholder="+60137003381" onChange={(v) => setSettings({ ...settings, whatsapp_number: v })} />
              <Field testid="settings-bank-name"  label="Bank name"                value={settings.bank_name} placeholder="Maybank"      onChange={(v) => setSettings({ ...settings, bank_name: v })} />
              <Field testid="settings-bank-acc-name" label="Account name"           value={settings.bank_account_name} placeholder="MIRESCO BIZ Sdn Bhd" onChange={(v) => setSettings({ ...settings, bank_account_name: v })} />
              <Field testid="settings-bank-acc-num"  label="Account number"         value={settings.bank_account_number} placeholder="1234567890"       onChange={(v) => setSettings({ ...settings, bank_account_number: v })} />
              <Field testid="settings-invoice-prefix" label="Invoice prefix"        value={settings.invoice_prefix} placeholder="INV-MIR"           onChange={(v) => setSettings({ ...settings, invoice_prefix: v })} />
              <div className="sm:col-span-2">
                <label className="overline text-zinc-500">Payment instructions shown in modal</label>
                <textarea value={settings.payment_instructions} onChange={(e) => setSettings({ ...settings, payment_instructions: e.target.value })} rows={2} data-testid="settings-payment-instructions" className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm focus:border-[#C8102E] focus:outline-none resize-none" />
              </div>
              <div className="sm:col-span-2">
                <label className="overline text-zinc-500">Receipt / confirmation message</label>
                <textarea value={settings.receipt_message} onChange={(e) => setSettings({ ...settings, receipt_message: e.target.value })} rows={2} data-testid="settings-receipt-message" className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm focus:border-[#C8102E] focus:outline-none resize-none" />
              </div>
            </div>
          </div>

          <div className="border border-zinc-200 bg-white p-6">
            <div className="overline text-zinc-500">Commission rates (%)</div>
            <h3 className="font-display font-black text-xl tracking-tight mt-1">Default splits</h3>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Field testid="settings-comm-aff"       label="Affiliate" value={settings.commission_affiliate_pct} onChange={(v) => setSettings({ ...settings, commission_affiliate_pct: v })} />
              <Field testid="settings-comm-agent"     label="Agent"     value={settings.commission_agent_pct}     onChange={(v) => setSettings({ ...settings, commission_agent_pct: v })} />
              <Field testid="settings-comm-stockist"  label="Stockist"  value={settings.commission_stockist_pct}  onChange={(v) => setSettings({ ...settings, commission_stockist_pct: v })} />
              <Field testid="settings-comm-vendor"    label="Vendor"    value={settings.commission_vendor_pct}    onChange={(v) => setSettings({ ...settings, commission_vendor_pct: v })} />
            </div>
          </div>

          <div className="border border-zinc-200 bg-white p-6">
            <div className="overline text-zinc-500">Payment gateway — placeholders</div>
            <h3 className="font-display font-black text-xl tracking-tight mt-1">Fill when you have keys (Billplz / ToyyibPay / SenangPay / Stripe)</h3>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field testid="settings-gw-provider"    label="Provider name (billplz / toyyibpay / senangpay / stripe)" value={settings.gateway_provider}     onChange={(v) => setSettings({ ...settings, gateway_provider: v })} />
              <Field testid="settings-gw-merchant"    label="Merchant ID"    value={settings.gateway_merchant_id}   onChange={(v) => setSettings({ ...settings, gateway_merchant_id: v })} />
              <Field testid="settings-gw-api"         label="API Key"        value={settings.gateway_api_key}       onChange={(v) => setSettings({ ...settings, gateway_api_key: v })} />
              <Field testid="settings-gw-secret"      label="Secret Key"     value={settings.gateway_secret_key}    onChange={(v) => setSettings({ ...settings, gateway_secret_key: v })} />
              <Field testid="settings-gw-callback"    label="Callback URL"   value={settings.gateway_callback_url}  onChange={(v) => setSettings({ ...settings, gateway_callback_url: v })} />
              <Field testid="settings-gw-return"      label="Return URL"     value={settings.gateway_return_url}    onChange={(v) => setSettings({ ...settings, gateway_return_url: v })} />
              <Field testid="settings-gw-webhook"     label="Webhook URL"    value={settings.gateway_webhook_url}   onChange={(v) => setSettings({ ...settings, gateway_webhook_url: v })} />
            </div>
          </div>

          <div className="border border-zinc-200 bg-white p-6">
            <div className="overline text-zinc-500">WhatsApp message templates</div>
            <h3 className="font-display font-black text-xl tracking-tight mt-1">Pre-filled messages for CTA buttons</h3>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                ["wa_tpl_make_money", "I Want to Make Money"],
                ["wa_tpl_promote_business", "Promote My Business"],
                ["wa_tpl_join_artist", "Join as Artist"],
                ["wa_tpl_join_affiliate", "Become Affiliate"],
                ["wa_tpl_join_stockist", "Become Stockist"],
                ["wa_tpl_book_campaign", "Book Campaign"],
                ["wa_tpl_contact", "Contact MIRESCO"],
                ["wa_tpl_payment_confirm", "Payment confirmation"],
              ].map(([k, l]) => (
                <div key={k} className="sm:col-span-2">
                  <label className="overline text-zinc-500">{l}</label>
                  <textarea value={settings[k] || ""} onChange={(e) => setSettings({ ...settings, [k]: e.target.value })} rows={2} data-testid={`settings-${k.replace(/_/g, "-")}`} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm focus:border-[#C8102E] focus:outline-none resize-none" />
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={async () => {
                setSettingsBusy(true);
                try {
                  await api.put("/admin/settings", settings);
                  toast.success("Saved — landing page updated.");
                  await load();
                } catch {
                  toast.error("Failed to save settings");
                } finally {
                  setSettingsBusy(false);
                }
              }}
              disabled={settingsBusy}
              data-testid="admin-settings-save"
              className="inline-flex items-center gap-2 bg-[#C8102E] hover:bg-[#9A0C24] text-white px-5 py-2.5 font-semibold text-sm transition-colors"
            >
              {settingsBusy ? "Saving…" : "Save all changes"}
            </button>
            <a href="/" target="_blank" rel="noopener noreferrer" className="text-xs text-zinc-500 hover:text-[#C8102E] underline" data-testid="admin-settings-preview">
              Preview landing →
            </a>
          </div>
        </div>
      )}

      {tab === "spotlights" && <SpotlightsTab api={api} />}
      {tab === "revenue" && <RevenueTab api={api} />}
      {tab === "artists" && <ArtistsTab api={api} />}
      {tab === "pathways" && <PathwaysTab api={api} />}
      {tab === "categories" && <CategoriesTab api={api} />}
      {tab === "opportunities-cms" && <BusinessOpportunitiesTab api={api} />}
      {tab === "leads" && <LeadsTab api={api} />}
      {tab === "journeys" && <JourneysTab api={api} />}
      {tab === "commissions" && <CommissionsTab api={api} />}
      {tab === "badges" && <BadgesTab api={api} />}
      {tab === "stories" && <SuccessStoriesTab api={api} />}
      {tab === "ads" && <AdPackagesTab api={api} />}
      {tab === "gateways" && <GatewaysTab api={api} />}

    </div>
  );
}

// ============================================================
// REVENUE TAB — payment requests + KPIs
// ============================================================
function RevenueTab({ api }) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [filter, setFilter] = useState("");
  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get(`/admin/payment-requests${filter ? `?status=${filter}` : ""}`),
        api.get("/admin/revenue-summary"),
      ]);
      setItems(Array.isArray(a.data) ? a.data : []);
      setSummary(b.data || {});
    } catch { toast.error("Failed to load revenue"); }
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/payment-requests/${id}/status`, { status });
      toast.success(`Marked ${status}`);
      await load();
    } catch { toast.error("Update failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-revenue-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Paid users"   value={summary.paid_users ?? 0}    testid="rev-stat-paid-users" />
        <Stat label="Paid"         value={summary.paid ?? 0}          testid="rev-stat-paid" />
        <Stat label="Pending"      value={summary.pending ?? 0}       testid="rev-stat-pending" />
        <Stat label="Failed / cancel" value={(summary.failed ?? 0) + (summary.cancelled ?? 0)} testid="rev-stat-failed" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {["", "pending", "paid", "failed", "cancelled", "refunded"].map((s) => (
          <button key={s || "all"} onClick={() => setFilter(s)} data-testid={`rev-filter-${s || "all"}`} className={`text-[10px] uppercase tracking-widest font-semibold px-3 py-1.5 border ${filter === s ? "bg-[#09090b] text-white border-[#09090b]" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900"}`}>{s || "all"}</button>
        ))}
      </div>
      <div className="bg-white border border-zinc-200">
        {items.length === 0 ? (
          <div className="p-10 text-center text-sm text-zinc-500">No payment requests {filter && `for status "${filter}"`}.</div>
        ) : (
          <ul className="divide-y divide-zinc-200">
            {items.map((it) => (
              <li key={it.request_id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`rev-row-${it.request_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold">{it.plan_name} · <span style={{ color: "#C8102E" }}>{it.amount}</span></div>
                  <div className="text-xs text-zinc-500 mt-0.5">{it.user_name || it.user_email} · {new Date(it.created_at).toLocaleString()}</div>
                  {it.invoice_id && <div className="text-[10px] text-zinc-500 mt-0.5 font-mono">{it.invoice_id}</div>}
                  {it.reference_note && <div className="text-[11px] text-zinc-600 mt-1">Note: {it.reference_note}</div>}
                </div>
                <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 border ${
                  it.status === "paid" ? "bg-green-100 text-green-900 border-green-300" :
                  it.status === "pending" ? "bg-yellow-100 text-yellow-900 border-yellow-300" :
                  "bg-zinc-100 text-zinc-700 border-zinc-300"
                }`}>{it.status}</span>
                <div className="flex items-center gap-1">
                  {it.status !== "paid" && <button onClick={() => setStatus(it.request_id, "paid")} data-testid={`rev-mark-paid-${it.request_id}`} className="text-xs uppercase tracking-widest font-semibold px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white">Mark paid</button>}
                  {it.status !== "failed" && <button onClick={() => setStatus(it.request_id, "failed")} data-testid={`rev-mark-failed-${it.request_id}`} className="text-xs uppercase tracking-widest font-semibold px-3 py-1.5 border border-zinc-300 hover:border-red-500 text-red-600">Fail</button>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ============================================================
// ARTISTS TAB — review artist signups
// ============================================================
function ArtistsTab({ api }) {
  const [items, setItems] = useState([]);
  const load = async () => { try { const r = await api.get("/admin/artists"); setItems(Array.isArray(r.data) ? r.data : []); } catch { toast.error("Failed to load artists"); } };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const setStatus = async (id, status) => { try { await api.put(`/admin/artists/${id}/status`, { status }); toast.success(status); await load(); } catch { toast.error("Update failed"); } };
  return (
    <div className="space-y-4" data-testid="admin-artists-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline text-zinc-500">Artist submissions</div>
          <h3 className="font-display font-black text-xl tracking-tight mt-1">Artists / Influencers</h3>
        </div>
      </div>
      <div className="bg-white border border-zinc-200">
        {items.length === 0 ? (
          <div className="p-10 text-center text-sm text-zinc-500">No artist signups yet. Direct people to <span className="font-mono">/artist/signup</span>.</div>
        ) : (
          <ul className="divide-y divide-zinc-200">
            {items.map((a) => (
              <li key={a.artist_id} className="p-4 flex flex-wrap items-start gap-4" data-testid={`artist-row-${a.artist_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold">{a.name} <span className="text-xs text-zinc-500 font-normal">· {a.category || "—"} · {a.followers || "—"} followers</span></div>
                  <div className="text-xs text-zinc-600 mt-0.5">{a.email} {a.whatsapp && `· ${a.whatsapp}`}</div>
                  <div className="text-[11px] text-zinc-500 mt-1 flex flex-wrap gap-x-3">
                    {a.instagram && <span>IG: {a.instagram}</span>}{a.tiktok && <span>TT: {a.tiktok}</span>}{a.facebook && <span>FB: {a.facebook}</span>}{a.youtube && <span>YT: {a.youtube}</span>}
                  </div>
                  {a.rate_note && <div className="text-[11px] text-zinc-700 mt-1">Rate: {a.rate_note}</div>}
                  {a.bio && <div className="text-[11px] text-zinc-600 mt-1 italic">"{a.bio}"</div>}
                </div>
                <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 border ${a.status === "approved" ? "bg-green-100 text-green-900 border-green-300" : a.status === "rejected" ? "bg-zinc-100 text-zinc-500 border-zinc-300" : "bg-yellow-100 text-yellow-900 border-yellow-300"}`}>{a.status}</span>
                <div className="flex items-center gap-1">
                  {a.status !== "approved" && <button onClick={() => setStatus(a.artist_id, "approved")} data-testid={`artist-approve-${a.artist_id}`} className="text-xs uppercase tracking-widest font-semibold px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white">Approve</button>}
                  {a.status !== "rejected" && <button onClick={() => setStatus(a.artist_id, "rejected")} data-testid={`artist-reject-${a.artist_id}`} className="text-xs uppercase tracking-widest font-semibold px-3 py-1.5 border border-zinc-300 hover:border-red-500 text-red-600">Reject</button>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ============================================================
// SPOTLIGHTS TAB — admin CRUD for landing-page spotlight calls
// ============================================================
const EMPTY_SPOTLIGHT = {
  title: "",
  subtitle: "",
  body: "",
  brand_tag: "",
  audience: "",
  cta_label: "Apply now",
  cta_url: "/opportunities",
  accent: "red",
  priority: 0,
  published: true,
};

function SpotlightsTab({ api }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);  // null = none, "new" = create, "<id>" = edit
  const [form, setForm] = useState(EMPTY_SPOTLIGHT);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/spotlights?only_published=false");
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("[SpotlightsTab] load failed:", e);
      toast.error("Failed to load spotlights");
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => { setForm(EMPTY_SPOTLIGHT); setEditing("new"); };
  const openEdit = (s) => {
    setForm({
      title: s.title || "",
      subtitle: s.subtitle || "",
      body: s.body || "",
      brand_tag: s.brand_tag || "",
      audience: s.audience || "",
      cta_label: s.cta_label || "Apply now",
      cta_url: s.cta_url || "/opportunities",
      accent: s.accent || "red",
      priority: s.priority ?? 0,
      published: s.published ?? true,
    });
    setEditing(s.spotlight_id);
  };
  const close = () => { setEditing(null); setForm(EMPTY_SPOTLIGHT); };

  const save = async () => {
    if (form.title.trim().length < 2) return toast.error("Title is required");
    setBusy(true);
    try {
      if (editing === "new") {
        await api.post("/spotlights", form);
        toast.success("Spotlight created — live on landing now.");
      } else {
        await api.put(`/spotlights/${editing}`, form);
        toast.success("Spotlight updated.");
      }
      await load();
      close();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.title}"?`)) return;
    try {
      await api.delete(`/spotlights/${s.spotlight_id}`);
      toast.success("Deleted");
      await load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const togglePublish = async (s) => {
    try {
      await api.put(`/spotlights/${s.spotlight_id}`, { ...s, published: !s.published });
      await load();
    } catch {
      toast.error("Failed to toggle");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-spotlights-panel">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-zinc-500">Landing-page shout-outs</div>
          <h3 className="font-display font-black text-xl tracking-tight mt-1">Spotlights</h3>
          <p className="text-xs text-zinc-500 mt-1 max-w-md">
            Create time-sensitive opportunity calls that appear in the Spotlight column on the landing page. Sorted by priority (higher first).
          </p>
        </div>
        <button onClick={openNew} data-testid="admin-spotlight-new" className="inline-flex items-center gap-2 bg-[#C8102E] hover:bg-[#9A0C24] text-white px-4 py-2 font-semibold text-sm">
          + New Spotlight
        </button>
      </div>

      <div className="bg-white border border-zinc-200">
        {items.length === 0 ? (
          <div className="p-10 text-center text-sm text-zinc-500">No spotlights yet. Click &quot;New Spotlight&quot; to add one.</div>
        ) : (
          <ul className="divide-y divide-zinc-200">
            {items.map((s) => (
              <li key={s.spotlight_id} className="p-5 flex flex-col sm:flex-row sm:items-start gap-4" data-testid={`admin-spotlight-row-${s.spotlight_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 text-white`} style={{ background: s.accent === "gold" ? "#E0AA3E" : s.accent === "charcoal" ? "#09090b" : "#C8102E" }}>
                      {s.audience || "Open"}
                    </span>
                    {s.brand_tag && <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">#{s.brand_tag}</span>}
                    {!s.published && <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold border border-zinc-300 px-1.5">draft</span>}
                    <span className="text-[10px] text-zinc-400">priority {s.priority}</span>
                  </div>
                  <div className="font-bold text-sm mt-1.5 leading-snug">{s.title}</div>
                  {s.subtitle && <div className="text-xs text-zinc-600 mt-0.5">{s.subtitle}</div>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => togglePublish(s)} data-testid={`admin-spotlight-toggle-${s.spotlight_id}`} className="text-xs uppercase tracking-widest font-semibold px-3 py-1.5 border border-zinc-300 hover:border-zinc-900">
                    {s.published ? "Unpublish" : "Publish"}
                  </button>
                  <button onClick={() => openEdit(s)} data-testid={`admin-spotlight-edit-${s.spotlight_id}`} className="text-xs uppercase tracking-widest font-semibold px-3 py-1.5 border border-zinc-300 hover:border-zinc-900">
                    Edit
                  </button>
                  <button onClick={() => remove(s)} data-testid={`admin-spotlight-delete-${s.spotlight_id}`} className="text-xs uppercase tracking-widest font-semibold px-3 py-1.5 text-red-600 hover:bg-red-50">
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4" onClick={close} data-testid="admin-spotlight-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-zinc-200 flex items-center justify-between">
              <h4 className="font-display font-black text-lg tracking-tight">{editing === "new" ? "New Spotlight" : "Edit Spotlight"}</h4>
              <button onClick={close} className="text-zinc-500 hover:text-zinc-900 text-sm">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <Field label="Title (the headline that gets shouted)" testid="admin-spotlight-field-title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} placeholder="e.g. Calling all teachers — earn with INOCHI" />
              <Field label="Subtitle (one-line punch under the title)" testid="admin-spotlight-field-subtitle" value={form.subtitle} onChange={(v) => setForm({ ...form, subtitle: v })} placeholder="e.g. Side income, your schedule." />
              <div>
                <label className="overline text-zinc-500">Body (full description, up to 600 chars)</label>
                <textarea
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  maxLength={600}
                  rows={4}
                  data-testid="admin-spotlight-field-body"
                  className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm focus:border-[#C8102E] focus:outline-none resize-none"
                />
                <div className="text-[10px] text-zinc-400 mt-1 text-right">{form.body.length}/600</div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Audience tag" testid="admin-spotlight-field-audience" value={form.audience} onChange={(v) => setForm({ ...form, audience: v })} placeholder="teachers, schools, housewives…" />
                <Field label="Brand tag" testid="admin-spotlight-field-brand" value={form.brand_tag} onChange={(v) => setForm({ ...form, brand_tag: v })} placeholder="inochi, nano-shield…" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="CTA button label" testid="admin-spotlight-field-cta-label" value={form.cta_label} onChange={(v) => setForm({ ...form, cta_label: v })} placeholder="Apply now" />
                <Field label="CTA link (/opportunities or https://…)" testid="admin-spotlight-field-cta-url" value={form.cta_url} onChange={(v) => setForm({ ...form, cta_url: v })} placeholder="/opportunities" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="overline text-zinc-500">Accent color</label>
                  <select
                    value={form.accent}
                    onChange={(e) => setForm({ ...form, accent: e.target.value })}
                    data-testid="admin-spotlight-field-accent"
                    className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm focus:border-[#C8102E] focus:outline-none"
                  >
                    <option value="red">Red (urgent)</option>
                    <option value="gold">Gold (premium)</option>
                    <option value="charcoal">Charcoal (sober)</option>
                  </select>
                </div>
                <Field label="Priority (higher = appears first)" testid="admin-spotlight-field-priority" value={String(form.priority)} onChange={(v) => setForm({ ...form, priority: parseInt(v || "0", 10) || 0 })} placeholder="100" />
              </div>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.published}
                  onChange={(e) => setForm({ ...form, published: e.target.checked })}
                  data-testid="admin-spotlight-field-published"
                  className="w-4 h-4"
                />
                Publish immediately (visible on landing page)
              </label>
            </div>
            <div className="p-6 border-t border-zinc-200 flex items-center justify-end gap-3">
              <button onClick={close} className="text-sm text-zinc-600 hover:text-zinc-900 font-semibold px-4 py-2">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="admin-spotlight-save" className="bg-[#C8102E] hover:bg-[#9A0C24] disabled:opacity-50 text-white px-5 py-2 text-sm font-semibold">
                {busy ? "Saving…" : (editing === "new" ? "Create" : "Save changes")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ============================================================
// PATHWAYS TAB — Homepage "What do you want to achieve today?"
// ============================================================
const PATHWAY_EMPTY = { title: "", subtitle: "", icon: "Sparkles", emoji: "", accent: "red", action_type: "route", action_target: "", wa_template: "", sort_order: 0, visible: true };

function PathwaysTab({ api }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // pathway_id | "new" | null
  const [form, setForm] = useState(PATHWAY_EMPTY);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const r = await api.get("/admin/pathways");
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  const open = (p) => { setEditing(p?.pathway_id || "new"); setForm(p ? { ...p } : PATHWAY_EMPTY); };
  const close = () => { setEditing(null); setForm(PATHWAY_EMPTY); };
  const save = async () => {
    setBusy(true);
    try {
      if (editing === "new") await api.post("/admin/pathways", form);
      else await api.put(`/admin/pathways/${editing}`, form);
      await load();
      toast.success("Pathway saved");
      close();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this pathway?")) return;
    try { await api.delete(`/admin/pathways/${id}`); await load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4" data-testid="admin-pathways-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-black text-2xl tracking-tight">Homepage Pathways</h2>
        <button onClick={() => open()} data-testid="admin-pathway-new" className="bg-[#C8102E] hover:bg-[#9A0C24] text-white px-4 py-2 text-sm font-semibold">+ New pathway</button>
      </div>
      <p className="text-sm text-zinc-500">These are the 6 intent cards on the homepage under &ldquo;What do you want to achieve today?&rdquo;. Add / edit / reorder freely.</p>
      <div className="border border-zinc-200 divide-y">
        {items.map((p) => (
          <div key={p.pathway_id} className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 grid place-items-center text-white text-sm font-bold" style={{ background: p.accent === "gold" ? "#D4AF37" : p.accent === "charcoal" ? "#1A1A1A" : p.accent === "green" ? "#0e7c66" : p.accent === "blue" ? "#1e3a8a" : "#C8102E" }}>{p.emoji || p.icon?.[0] || "?"}</div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate">{p.title}</div>
              <div className="text-xs text-zinc-500 truncate">{p.subtitle}</div>
              <div className="text-[10px] text-zinc-400 mt-0.5">{p.action_type} → {p.action_target || "(WA)"} · order {p.sort_order} · {p.visible ? "visible" : "hidden"}</div>
            </div>
            <button onClick={() => open(p)} className="text-xs font-semibold text-zinc-700 hover:text-[#C8102E] px-3 py-1.5 border border-zinc-300" data-testid={`admin-pathway-edit-${p.pathway_id}`}>Edit</button>
            <button onClick={() => del(p.pathway_id)} className="text-xs font-semibold text-red-700 hover:text-red-900 px-3 py-1.5 border border-red-200" data-testid={`admin-pathway-delete-${p.pathway_id}`}>Delete</button>
          </div>
        ))}
        {items.length === 0 && <div className="p-8 text-center text-sm text-zinc-500">No pathways yet.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={close}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-pathway-modal">
            <div className="p-5 border-b border-zinc-200 flex items-center justify-between">
              <div className="font-display font-black text-lg">{editing === "new" ? "New Pathway" : "Edit Pathway"}</div>
              <button onClick={close}><XIcon className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <Field label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} placeholder="I Want Extra Income" testid="pw-title" />
              <Field label="Subtitle" value={form.subtitle} onChange={(v) => setForm({ ...form, subtitle: v })} placeholder="Start earning from RM 0…" testid="pw-subtitle" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Icon (Lucide)" value={form.icon} onChange={(v) => setForm({ ...form, icon: v })} placeholder="Wallet" testid="pw-icon" />
                <Field label="Emoji (optional)" value={form.emoji} onChange={(v) => setForm({ ...form, emoji: v })} placeholder="💰" testid="pw-emoji" />
              </div>
              <div>
                <label className="overline text-zinc-500">Accent</label>
                <select value={form.accent} onChange={(e) => setForm({ ...form, accent: e.target.value })} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm" data-testid="pw-accent">
                  <option value="red">Red</option><option value="gold">Gold</option><option value="charcoal">Charcoal</option><option value="green">Green</option><option value="blue">Blue</option>
                </select>
              </div>
              <div>
                <label className="overline text-zinc-500">Action Type</label>
                <select value={form.action_type} onChange={(e) => setForm({ ...form, action_type: e.target.value })} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm" data-testid="pw-action-type">
                  <option value="route">Route (internal path)</option><option value="whatsapp">WhatsApp</option><option value="external">External URL</option>
                </select>
              </div>
              <Field label="Action Target (path or URL)" value={form.action_target} onChange={(v) => setForm({ ...form, action_target: v })} placeholder="/opportunities-directory?investment_level=free" testid="pw-action-target" />
              <div>
                <label className="overline text-zinc-500">WhatsApp Template (used if action = WhatsApp)</label>
                <textarea value={form.wa_template} onChange={(e) => setForm({ ...form, wa_template: e.target.value })} rows={3} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm" data-testid="pw-wa-template" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Sort order" value={form.sort_order} onChange={(v) => setForm({ ...form, sort_order: Number(v) || 0 })} testid="pw-sort" />
                <label className="flex items-center gap-2 text-sm mt-6">
                  <input type="checkbox" checked={form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })} data-testid="pw-visible" /> Visible
                </label>
              </div>
            </div>
            <div className="p-4 border-t border-zinc-200 flex items-center justify-end gap-2">
              <button onClick={close} className="px-4 py-2 text-sm font-semibold text-zinc-600 hover:text-zinc-900">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="admin-pathway-save" className="bg-[#C8102E] hover:bg-[#9A0C24] disabled:opacity-50 text-white px-5 py-2 text-sm font-semibold">{busy ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// CATEGORIES TAB — Business categories
// ============================================================
const CATEGORY_EMPTY = { name: "", slug: "", icon: "Briefcase", description: "", sort_order: 0, visible: true };

function CategoriesTab({ api }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(CATEGORY_EMPTY);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const r = await api.get("/admin/categories"); setItems(Array.isArray(r.data) ? r.data : []); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  const open = (c) => { setEditing(c?.category_id || "new"); setForm(c ? { ...c } : CATEGORY_EMPTY); };
  const close = () => { setEditing(null); setForm(CATEGORY_EMPTY); };
  const save = async () => {
    setBusy(true);
    try {
      if (editing === "new") await api.post("/admin/categories", form);
      else await api.put(`/admin/categories/${editing}`, form);
      await load(); close(); toast.success("Category saved");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try { await api.delete(`/admin/categories/${id}`); await load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4" data-testid="admin-categories-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-black text-2xl tracking-tight">Business Categories</h2>
        <button onClick={() => open()} data-testid="admin-cat-new" className="bg-[#C8102E] hover:bg-[#9A0C24] text-white px-4 py-2 text-sm font-semibold">+ New category</button>
      </div>
      <p className="text-sm text-zinc-500">Unlimited categories — used across the platform for filtering opportunities.</p>
      <div className="border border-zinc-200 divide-y">
        {items.map((c) => (
          <div key={c.category_id} className="p-4 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">{c.name} <span className="text-xs text-zinc-400 font-mono ml-2">/{c.slug}</span></div>
              <div className="text-xs text-zinc-500">{c.description || "—"} · icon: {c.icon} · order {c.sort_order}</div>
            </div>
            <button onClick={() => open(c)} className="text-xs font-semibold text-zinc-700 hover:text-[#C8102E] px-3 py-1.5 border border-zinc-300" data-testid={`admin-cat-edit-${c.category_id}`}>Edit</button>
            <button onClick={() => del(c.category_id)} className="text-xs font-semibold text-red-700 hover:text-red-900 px-3 py-1.5 border border-red-200">Delete</button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={close}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md" data-testid="admin-cat-modal">
            <div className="p-5 border-b border-zinc-200 flex items-center justify-between">
              <div className="font-display font-black text-lg">{editing === "new" ? "New Category" : "Edit Category"}</div>
              <button onClick={close}><XIcon className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="cat-name" />
              <Field label="Slug (unique)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v.toLowerCase().replace(/\s+/g, "-") })} testid="cat-slug" />
              <Field label="Icon (Lucide)" value={form.icon} onChange={(v) => setForm({ ...form, icon: v })} testid="cat-icon" />
              <div>
                <label className="overline text-zinc-500">Description</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm" data-testid="cat-desc" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Sort order" value={form.sort_order} onChange={(v) => setForm({ ...form, sort_order: Number(v) || 0 })} />
                <label className="flex items-center gap-2 text-sm mt-6">
                  <input type="checkbox" checked={form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })} /> Visible
                </label>
              </div>
            </div>
            <div className="p-4 border-t border-zinc-200 flex items-center justify-end gap-2">
              <button onClick={close} className="px-4 py-2 text-sm font-semibold text-zinc-600 hover:text-zinc-900">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="admin-cat-save" className="bg-[#C8102E] hover:bg-[#9A0C24] disabled:opacity-50 text-white px-5 py-2 text-sm font-semibold">{busy ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// BUSINESS OPPORTUNITIES TAB — Universal 15-section CMS editor
// ============================================================
const TAB_EMPTY = {
  enabled: true, heading: "", subheading: "", body: "", bullets: [],
  video_url: "", image_url: "",
  cta_label: "", cta_kind: "whatsapp", cta_url: "", wa_template: "",
  journey_slug: "",
};

const OPP_EMPTY = {
  slug: "", title: "", brand: "", category_slug: "", opportunity_nature: "", revenue_model: "",
  tagline: "", hero_image_url: "", hero_video_url: "",
  summary: "", overview: "", investment_required: "", estimated_income: "", business_model: "",
  requirements: [], training: "",
  videos: [], photos: [], testimonials: [], success_stories: [],
  faq: [], ctas: [],
  calculator: { enabled: false, retail_price: 0, your_cost: 0, profit_per_unit: 0, default_units_per_day: 1, days_per_month: 30, currency: "RM", notes: "" },
  tab_start_earning: { ...TAB_EMPTY, cta_kind: "affiliate", cta_label: "Start Earning", journey_slug: "inochi-affiliate" },
  tab_creator_campaign: { ...TAB_EMPTY, cta_kind: "whatsapp", cta_label: "Apply as Creator", journey_slug: "creator-partner" },
  countries: ["Malaysia"], states: [],
  investment_level: "free", income_potential: "medium",
  opportunity_types: [],
  whatsapp_number: "60137003381",
  featured: false, priority: 0, sort_order: 0, published: true,
};

function BusinessOpportunitiesTab({ api }) {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(OPP_EMPTY);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const [a, b] = await Promise.all([api.get("/admin/business-opportunities"), api.get("/admin/categories")]);
      setItems(Array.isArray(a.data) ? a.data : []);
      setCats(Array.isArray(b.data) ? b.data : []);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  const open = (o) => {
    setEditing(o?.opportunity_id || "new");
    setForm(o ? {
      ...OPP_EMPTY, ...o,
      calculator: { ...OPP_EMPTY.calculator, ...(o.calculator || {}) },
      tab_start_earning: { ...OPP_EMPTY.tab_start_earning, ...(o.tab_start_earning || {}) },
      tab_creator_campaign: { ...OPP_EMPTY.tab_creator_campaign, ...(o.tab_creator_campaign || {}) },
    } : OPP_EMPTY);
  };
  const close = () => { setEditing(null); setForm(OPP_EMPTY); };
  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      // sanitize opportunity_types (comma-separated string OK)
      if (typeof payload.opportunity_types === "string") payload.opportunity_types = payload.opportunity_types.split(",").map((s) => s.trim()).filter(Boolean);
      if (typeof payload.requirements === "string") payload.requirements = payload.requirements.split("\n").map((s) => s.trim()).filter(Boolean);
      // sanitize tab bullets (newline-separated string OK)
      for (const k of ["tab_start_earning", "tab_creator_campaign"]) {
        if (payload[k] && typeof payload[k].bullets === "string") {
          payload[k] = { ...payload[k], bullets: payload[k].bullets.split("\n").map((s) => s.trim()).filter(Boolean) };
        }
      }
      if (editing === "new") await api.post("/admin/business-opportunities", payload);
      else await api.put(`/admin/business-opportunities/${editing}`, payload);
      await load(); close(); toast.success("Opportunity saved");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this opportunity?")) return;
    try { await api.delete(`/admin/business-opportunities/${id}`); await load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="admin-opps-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-black text-2xl tracking-tight">Business Opportunities</h2>
        <button onClick={() => open()} data-testid="admin-opp-new" className="bg-[#C8102E] hover:bg-[#9A0C24] text-white px-4 py-2 text-sm font-semibold">+ New opportunity</button>
      </div>
      <p className="text-sm text-zinc-500">Every important field is editable — no code needed. Supports unlimited opportunities.</p>
      <div className="border border-zinc-200 divide-y">
        {items.map((o) => (
          <div key={o.opportunity_id} className="p-4 flex items-center gap-4">
            <div className="w-14 h-14 bg-zinc-100 border border-zinc-200 shrink-0 grid place-items-center text-[10px] text-zinc-500 font-mono overflow-hidden">
              {o.hero_image_url ? <img src={o.hero_image_url} alt="" className="w-full h-full object-cover" /> : "no img"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate">{o.title} <span className="text-xs text-zinc-400 font-mono ml-2">/{o.slug}</span></div>
              <div className="text-xs text-zinc-500 truncate">{o.brand} · {o.category_slug} · {o.opportunity_types?.join(", ") || "—"} · {o.published ? "published" : "draft"}{o.featured ? " · ⭐ featured" : ""}</div>
            </div>
            <a href={`/opportunity/${o.slug}`} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-zinc-500 hover:text-[#C8102E] underline">View</a>
            <button onClick={() => open(o)} className="text-xs font-semibold text-zinc-700 hover:text-[#C8102E] px-3 py-1.5 border border-zinc-300" data-testid={`admin-opp-edit-${o.opportunity_id}`}>Edit</button>
            <button onClick={() => del(o.opportunity_id)} className="text-xs font-semibold text-red-700 hover:text-red-900 px-3 py-1.5 border border-red-200">Delete</button>
          </div>
        ))}
        {items.length === 0 && <div className="p-8 text-center text-sm text-zinc-500">No opportunities yet.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={close}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="admin-opp-modal">
            <div className="p-5 border-b border-zinc-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="font-display font-black text-lg">{editing === "new" ? "New Opportunity" : "Edit Opportunity"}</div>
              <button onClick={close}><XIcon className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-6">
              {/* Identity */}
              <div className="space-y-3">
                <div className="text-xs uppercase tracking-widest font-semibold text-zinc-500">Identity</div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Slug (unique, URL)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v.toLowerCase().replace(/\s+/g, "-") })} testid="opp-slug" />
                  <Field label="Brand" value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} />
                </div>
                <Field label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="opp-title" />
                <Field label="Tagline" value={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="overline text-zinc-500">Category</label>
                    <select value={form.category_slug} onChange={(e) => setForm({ ...form, category_slug: e.target.value })} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm" data-testid="opp-category">
                      <option value="">—</option>
                      {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                    </select>
                  </div>
                  <Field label="Hero image URL" value={form.hero_image_url} onChange={(v) => setForm({ ...form, hero_image_url: v })} placeholder="/brand/... or https://" />
                </div>
                <Field label="Opportunity Nature (short label)" value={form.opportunity_nature} onChange={(v) => setForm({ ...form, opportunity_nature: v })} placeholder="Product · Skincare · Affiliate ready" />
                <div>
                  <label className="overline text-zinc-500">Revenue Model</label>
                  <textarea value={form.revenue_model} onChange={(e) => setForm({ ...form, revenue_model: e.target.value })} rows={2} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm" data-testid="opp-revenue" />
                </div>
              </div>

              {/* 15-section content */}
              <div className="space-y-3">
                <div className="text-xs uppercase tracking-widest font-semibold text-zinc-500">Content (15-Section Template)</div>
                <div><label className="overline text-zinc-500">1. Business Summary</label><textarea value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} rows={2} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm" /></div>
                <div><label className="overline text-zinc-500">2. Opportunity Overview</label><textarea value={form.overview} onChange={(e) => setForm({ ...form, overview: e.target.value })} rows={4} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm" /></div>
                <Field label="5. Investment Required" value={form.investment_required} onChange={(v) => setForm({ ...form, investment_required: v })} />
                <Field label="6. Estimated Income" value={form.estimated_income} onChange={(v) => setForm({ ...form, estimated_income: v })} />
                <div><label className="overline text-zinc-500">7. Business Model</label><textarea value={form.business_model} onChange={(e) => setForm({ ...form, business_model: e.target.value })} rows={3} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm" /></div>
                <div><label className="overline text-zinc-500">8. Requirements (one per line)</label><textarea value={Array.isArray(form.requirements) ? form.requirements.join("\n") : form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} rows={3} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm font-mono" /></div>
                <div><label className="overline text-zinc-500">9. Training</label><textarea value={form.training} onChange={(e) => setForm({ ...form, training: e.target.value })} rows={2} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm" /></div>
              </div>

              {/* JSON blocks for advanced arrays */}
              <div className="space-y-3">
                <div className="text-xs uppercase tracking-widest font-semibold text-zinc-500">Media &amp; Testimonials (JSON — arrays)</div>
                {["videos","photos","testimonials","success_stories","faq","ctas"].map((k) => (
                  <div key={k}>
                    <label className="overline text-zinc-500">{k}</label>
                    <textarea
                      value={JSON.stringify(form[k] || [], null, 2)}
                      onChange={(e) => {
                        try { const j = JSON.parse(e.target.value); setForm({ ...form, [k]: j }); }
                        catch { setForm({ ...form, [k]: e.target.value }); }
                      }}
                      rows={k === "ctas" || k === "faq" ? 6 : 4}
                      className="mt-2 w-full border border-zinc-300 px-3 py-2 text-xs font-mono"
                      data-testid={`opp-${k}`}
                    />
                  </div>
                ))}
              </div>

              {/* Calculator */}
              <div className="space-y-3">
                <div className="text-xs uppercase tracking-widest font-semibold text-zinc-500">Income Calculator</div>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.calculator?.enabled} onChange={(e) => setForm({ ...form, calculator: { ...form.calculator, enabled: e.target.checked } })} data-testid="opp-calc-enabled" /> Show calculator on page</label>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Retail price" value={form.calculator?.retail_price ?? 0} onChange={(v) => setForm({ ...form, calculator: { ...form.calculator, retail_price: Number(v) || 0 } })} />
                  <Field label="Your cost" value={form.calculator?.your_cost ?? 0} onChange={(v) => setForm({ ...form, calculator: { ...form.calculator, your_cost: Number(v) || 0 } })} />
                  <Field label="Default units / day" value={form.calculator?.default_units_per_day ?? 1} onChange={(v) => setForm({ ...form, calculator: { ...form.calculator, default_units_per_day: Number(v) || 0 } })} />
                  <Field label="Days / month" value={form.calculator?.days_per_month ?? 30} onChange={(v) => setForm({ ...form, calculator: { ...form.calculator, days_per_month: Number(v) || 0 } })} />
                  <Field label="Currency" value={form.calculator?.currency ?? "RM"} onChange={(v) => setForm({ ...form, calculator: { ...form.calculator, currency: v } })} />
                </div>
                <div><label className="overline text-zinc-500">Calculator notes / disclaimer</label><textarea value={form.calculator?.notes || ""} onChange={(e) => setForm({ ...form, calculator: { ...form.calculator, notes: e.target.value } })} rows={2} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm" /></div>
              </div>

              {/* Engagement Tabs (Guided Growth) — CMS-driven Start Earning + Creator Campaign */}
              <div className="space-y-3">
                <div className="text-xs uppercase tracking-widest font-semibold text-zinc-500">Engagement Tabs (Guided Growth)</div>
                <p className="text-xs text-zinc-500">These render as 2 of the 3 tabs on the Opportunity page. Tab 1 (Business Opportunity) uses the 15-section content above.</p>

                {[
                  { key: "tab_start_earning",    title: "Tab 2 — Start Earning",     testid: "opp-tab-se"   },
                  { key: "tab_creator_campaign", title: "Tab 3 — Creator Campaign", testid: "opp-tab-cc"   },
                ].map(({ key, title, testid }) => {
                  const t = form[key] || {};
                  const patch = (patchObj) => setForm({ ...form, [key]: { ...t, ...patchObj } });
                  return (
                    <div key={key} className="border border-zinc-200 p-4 space-y-3" data-testid={`admin-${testid}`}>
                      <div className="flex items-center justify-between">
                        <div className="font-semibold text-sm">{title}</div>
                        <label className="flex items-center gap-2 text-xs">
                          <input type="checkbox" checked={t.enabled !== false} onChange={(e) => patch({ enabled: e.target.checked })} data-testid={`${testid}-enabled`} /> Enabled
                        </label>
                      </div>
                      <Field label="Heading" value={t.heading || ""} onChange={(v) => patch({ heading: v })} testid={`${testid}-heading`} />
                      <Field label="Subheading" value={t.subheading || ""} onChange={(v) => patch({ subheading: v })} />
                      <div>
                        <label className="overline text-zinc-500">Body</label>
                        <textarea value={t.body || ""} onChange={(e) => patch({ body: e.target.value })} rows={4} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm" data-testid={`${testid}-body`} />
                      </div>
                      <div>
                        <label className="overline text-zinc-500">Bullets (one per line)</label>
                        <textarea
                          value={Array.isArray(t.bullets) ? t.bullets.join("\n") : (t.bullets || "")}
                          onChange={(e) => patch({ bullets: e.target.value })}
                          rows={5}
                          className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm font-mono"
                          data-testid={`${testid}-bullets`}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Image URL" value={t.image_url || ""} onChange={(v) => patch({ image_url: v })} placeholder="/uploads/... or https://" />
                        <Field label="Video URL" value={t.video_url || ""} onChange={(v) => patch({ video_url: v })} placeholder="https://…" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="CTA Label" value={t.cta_label || ""} onChange={(v) => patch({ cta_label: v })} placeholder="Start Earning" />
                        <div>
                          <label className="overline text-zinc-500">CTA Kind</label>
                          <select value={t.cta_kind || "whatsapp"} onChange={(e) => patch({ cta_kind: e.target.value })} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm">
                            {["buy","affiliate","agent","stockist","distributor","kiosk","franchise","territory_partner","investor","apply","whatsapp","external","learn"].map((k) => <option key={k} value={k}>{k}</option>)}
                          </select>
                        </div>
                      </div>
                      <Field label="CTA URL (for external / buy)" value={t.cta_url || ""} onChange={(v) => patch({ cta_url: v })} placeholder="https://…" />
                      <div>
                        <label className="overline text-zinc-500">WhatsApp Template</label>
                        <textarea value={t.wa_template || ""} onChange={(e) => patch({ wa_template: e.target.value })} rows={2} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm" data-testid={`${testid}-wa`} />
                      </div>
                      <Field label="Linked Journey slug (optional)" value={t.journey_slug || ""} onChange={(v) => patch({ journey_slug: v })} placeholder="inochi-affiliate" testid={`${testid}-journey`} />
                    </div>
                  );
                })}
              </div>

              {/* Filters */}
              <div className="space-y-3">
                <div className="text-xs uppercase tracking-widest font-semibold text-zinc-500">Filters &amp; Contact</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="overline text-zinc-500">Investment level</label>
                    <select value={form.investment_level} onChange={(e) => setForm({ ...form, investment_level: e.target.value })} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm">
                      <option value="free">Free</option><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="premium">Premium</option>
                    </select>
                  </div>
                  <div>
                    <label className="overline text-zinc-500">Income potential</label>
                    <select value={form.income_potential} onChange={(e) => setForm({ ...form, income_potential: e.target.value })} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm">
                      <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="very_high">Very high</option>
                    </select>
                  </div>
                </div>
                <Field
                  label="Opportunity types (comma-separated)"
                  value={Array.isArray(form.opportunity_types) ? form.opportunity_types.join(", ") : form.opportunity_types}
                  onChange={(v) => setForm({ ...form, opportunity_types: v })}
                  placeholder="affiliate, agent, stockist"
                />
                <Field label="WhatsApp number for this opportunity" value={form.whatsapp_number} onChange={(v) => setForm({ ...form, whatsapp_number: v })} placeholder="60137003381" />
              </div>

              {/* Visibility */}
              <div className="grid grid-cols-3 gap-3">
                <Field label="Priority" value={form.priority} onChange={(v) => setForm({ ...form, priority: Number(v) || 0 })} />
                <Field label="Sort order" value={form.sort_order} onChange={(v) => setForm({ ...form, sort_order: Number(v) || 0 })} />
                <div className="flex items-center gap-4 mt-6">
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured</label>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published</label>
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-zinc-200 flex items-center justify-end gap-2 sticky bottom-0 bg-white">
              <button onClick={close} className="px-4 py-2 text-sm font-semibold text-zinc-600 hover:text-zinc-900">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="admin-opp-save" className="bg-[#C8102E] hover:bg-[#9A0C24] disabled:opacity-50 text-white px-5 py-2 text-sm font-semibold">{busy ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// LEADS TAB — Revenue Conversion Engine (Mini CRM)
// Every enquiry becomes a trackable lead. Filter · Assign · Follow-up · Notes · CSV export.
// ============================================================
const LEAD_STATUS_STYLES = {
  new:        { label: "New",        bg: "bg-blue-50",    fg: "text-blue-700",    border: "border-blue-200" },
  contacted:  { label: "Contacted",  bg: "bg-purple-50",  fg: "text-purple-700",  border: "border-purple-200" },
  qualified:  { label: "Qualified",  bg: "bg-amber-50",   fg: "text-amber-700",   border: "border-amber-200" },
  converted:  { label: "Converted",  bg: "bg-emerald-50", fg: "text-emerald-700", border: "border-emerald-200" },
  lost:       { label: "Lost",       bg: "bg-zinc-100",   fg: "text-zinc-500",    border: "border-zinc-200" },
  on_hold:    { label: "On Hold",    bg: "bg-orange-50",  fg: "text-orange-700",  border: "border-orange-200" },
};

function relTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return "—"; }
}

function LeadsTab({ api }) {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [staff, setStaff] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [assignedFilter, setAssignedFilter] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const qs = new URLSearchParams();
      if (statusFilter) qs.set("status", statusFilter);
      if (assignedFilter) qs.set("assigned_to", assignedFilter);
      if (q.trim()) qs.set("q", q.trim());
      const [a, b, c] = await Promise.all([
        api.get(`/admin/opportunity-leads${qs.toString() ? "?" + qs.toString() : ""}`),
        api.get("/admin/opportunity-leads/stats"),
        api.get("/admin/opportunity-leads/staff"),
      ]);
      setItems(Array.isArray(a.data) ? a.data : []);
      setStats(b.data || {});
      setStaff(Array.isArray(c.data) ? c.data : []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [statusFilter, assignedFilter, q]);

  const patchLead = async (leadId, patch) => {
    setBusy(true);
    try {
      const r = await api.patch(`/admin/opportunity-leads/${leadId}`, patch);
      setItems((prev) => prev.map((l) => (l.lead_id === leadId ? r.data : l)));
      if (selected?.lead_id === leadId) setSelected(r.data);
      toast.success("Lead updated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const del = async (leadId) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.delete(`/admin/opportunity-leads/${leadId}`);
      setItems((prev) => prev.filter((l) => l.lead_id !== leadId));
      setSelected(null);
      toast.success("Lead deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const exportCsv = () => {
    const qs = new URLSearchParams();
    if (statusFilter) qs.set("status", statusFilter);
    const url = `${api.defaults.baseURL}/admin/opportunity-leads/export.csv${qs.toString() ? "?" + qs.toString() : ""}`;
    // authenticated download via fetch → blob (respects cookie)
    fetch(url, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error("Export failed"); return r.blob(); })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `miresco-leads-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      })
      .catch(() => toast.error("CSV export failed"));
  };

  const waLink = (lead) => {
    const digits = (lead.phone || "").replace(/\D/g, "");
    const first = (lead.name || "").trim().split(" ")[0] || "";
    const tpl = `Hi ${first}! Thanks for your enquiry on MIRESCO (${lead.opportunity_slug}). How can I help you?`;
    return digits ? `https://wa.me/${digits}?text=${encodeURIComponent(tpl)}` : "";
  };

  return (
    <div className="space-y-5" data-testid="admin-leads-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display font-black text-2xl tracking-tight">Lead Inbox</h2>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} data-testid="admin-leads-export" className="text-xs uppercase tracking-widest font-semibold border border-zinc-300 hover:border-zinc-900 px-3 py-2">
            Export CSV
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="admin-leads-stats">
        <Stat label="Total" value={stats.total ?? "—"} testid="lead-stat-total" />
        {["new", "contacted", "qualified", "converted", "lost"].map((k) => (
          <Stat key={k} label={LEAD_STATUS_STYLES[k].label} value={stats.by_status?.[k] ?? 0} testid={`lead-stat-${k}`} />
        ))}
        <Stat label="Conv %" value={`${stats.conversion_rate ?? 0}%`} testid="lead-stat-conv" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="border border-zinc-200 p-4 bg-white">
          <div className="overline text-zinc-500">Due follow-ups</div>
          <div className="font-display font-black text-3xl mt-2 text-red-700" data-testid="lead-stat-due">{stats.due_follow_ups ?? 0}</div>
        </div>
        <div className="border border-zinc-200 p-4 bg-white">
          <div className="overline text-zinc-500">Leads · last 7d</div>
          <div className="font-display font-black text-3xl mt-2">{stats.leads_7d ?? 0}</div>
        </div>
        <div className="border border-zinc-200 p-4 bg-white">
          <div className="overline text-zinc-500">Leads · last 30d</div>
          <div className="font-display font-black text-3xl mt-2">{stats.leads_30d ?? 0}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2" data-testid="admin-leads-filters">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, phone, email…"
          className="border border-zinc-300 px-3 py-2 text-sm"
          data-testid="admin-leads-search"
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-zinc-300 px-3 py-2 text-sm bg-white" data-testid="admin-leads-status-filter">
          <option value="">All statuses</option>
          {Object.entries(LEAD_STATUS_STYLES).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
        </select>
        <select value={assignedFilter} onChange={(e) => setAssignedFilter(e.target.value)} className="border border-zinc-300 px-3 py-2 text-sm bg-white" data-testid="admin-leads-assigned-filter">
          <option value="">All assignees</option>
          <option value="unassigned">Unassigned</option>
          {staff.map((s) => <option key={s.user_id} value={s.user_id}>{s.name || s.email}</option>)}
        </select>
        {(statusFilter || assignedFilter || q) && (
          <button onClick={() => { setStatusFilter(""); setAssignedFilter(""); setQ(""); }} className="border-2 border-zinc-900 px-3 py-2 text-sm font-semibold hover:bg-zinc-900 hover:text-white">Clear</button>
        )}
      </div>

      {/* Table */}
      <div className="border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-leads-table">
          <thead className="bg-zinc-50 text-left text-xs uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="p-3">Received</th>
              <th className="p-3">Name / Phone</th>
              <th className="p-3">Opportunity</th>
              <th className="p-3">CTA</th>
              <th className="p-3">Status</th>
              <th className="p-3">Assignee</th>
              <th className="p-3">Follow-up</th>
              <th className="p-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {items.map((l) => {
              const st = LEAD_STATUS_STYLES[l.status] || LEAD_STATUS_STYLES.new;
              return (
                <tr key={l.lead_id} className="border-t border-zinc-200 hover:bg-zinc-50 cursor-pointer" onClick={() => setSelected(l)} data-testid={`admin-lead-row-${l.lead_id}`}>
                  <td className="p-3 whitespace-nowrap text-zinc-500">{relTime(l.created_at)}</td>
                  <td className="p-3">
                    <div className="font-semibold">{l.name || <span className="text-zinc-400 italic">no name</span>}</div>
                    <div className="text-xs text-zinc-500 font-mono">{l.phone || l.email || "—"}</div>
                  </td>
                  <td className="p-3">
                    <a href={`/opportunity/${l.opportunity_slug}`} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold underline hover:text-red-700" onClick={(e) => e.stopPropagation()}>{l.opportunity_slug}</a>
                  </td>
                  <td className="p-3 text-xs uppercase tracking-widest text-zinc-600">{l.cta_kind}</td>
                  <td className="p-3">
                    <span className={`inline-flex items-center px-2 py-0.5 border text-[10px] uppercase font-semibold tracking-widest ${st.bg} ${st.fg} ${st.border}`}>{st.label}</span>
                  </td>
                  <td className="p-3 text-xs text-zinc-600">{l.assigned_to_name || <span className="text-zinc-400 italic">unassigned</span>}</td>
                  <td className="p-3 text-xs text-zinc-600">{l.follow_up_at ? new Date(l.follow_up_at).toLocaleString() : "—"}</td>
                  <td className="p-3 text-right">
                    {l.phone && (
                      <a
                        href={waLink(l)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-white px-2 py-1"
                        style={{ background: "#25D366" }}
                        data-testid={`admin-lead-wa-${l.lead_id}`}
                      >WA</a>
                    )}
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan="8" className="p-10 text-center text-sm text-zinc-500">No leads match the current filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Detail drawer */}
      {selected && (
        <LeadDetailDrawer
          lead={selected}
          staff={staff}
          onClose={() => setSelected(null)}
          onPatch={patchLead}
          onDelete={del}
          busy={busy}
        />
      )}
    </div>
  );
}

function LeadDetailDrawer({ lead, staff, onClose, onPatch, onDelete, busy }) {
  const [noteText, setNoteText] = useState("");
  const [followUp, setFollowUp] = useState(lead.follow_up_at ? lead.follow_up_at.slice(0, 16) : "");
  const [assignee, setAssignee] = useState(lead.assigned_to || "");
  const st = LEAD_STATUS_STYLES[lead.status] || LEAD_STATUS_STYLES.new;
  const waDigits = (lead.phone || "").replace(/\D/g, "");
  const first = (lead.name || "").trim().split(" ")[0] || "";
  const waTemplate = `Hi ${first}! Thanks for your enquiry on MIRESCO (${lead.opportunity_slug}). How can I help you?`;
  const waLink = waDigits ? `https://wa.me/${waDigits}?text=${encodeURIComponent(waTemplate)}` : "";

  const addNote = async () => {
    if (!noteText.trim()) return;
    await onPatch(lead.lead_id, { add_note: noteText.trim() });
    setNoteText("");
  };

  const saveFollowUp = async () => {
    // ISO with timezone
    const iso = followUp ? new Date(followUp).toISOString() : "";
    await onPatch(lead.lead_id, { follow_up_at: iso });
  };

  const saveAssignee = async () => {
    await onPatch(lead.lead_id, { assigned_to: assignee });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={onClose} data-testid="admin-lead-drawer">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="p-5 border-b border-zinc-200 sticky top-0 bg-white z-10 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Lead · {lead.lead_id}</div>
            <div className="font-display font-black text-xl mt-1">{lead.name || <span className="text-zinc-400 italic">Anonymous enquiry</span>}</div>
            <div className="text-xs text-zinc-500 mt-1">{lead.opportunity_slug} · {lead.cta_kind} · {relTime(lead.created_at)}</div>
          </div>
          <button onClick={onClose}><XIcon className="w-5 h-5" /></button>
        </div>

        {/* Status pills */}
        <div className="p-5 border-b border-zinc-200">
          <div className="overline text-zinc-500 mb-2">Status</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(LEAD_STATUS_STYLES).map(([k, s]) => (
              <button
                key={k}
                onClick={() => onPatch(lead.lead_id, { status: k })}
                disabled={busy || lead.status === k}
                className={`text-xs uppercase tracking-widest font-semibold px-3 py-1.5 border ${lead.status === k ? `${s.bg} ${s.fg} ${s.border}` : "border-zinc-300 text-zinc-500 hover:border-zinc-900"}`}
                data-testid={`admin-lead-status-${k}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Contact */}
        <div className="p-5 border-b border-zinc-200 space-y-2">
          <div className="overline text-zinc-500">Contact</div>
          <div className="text-sm"><span className="text-zinc-500 w-20 inline-block">Name</span> <span className="font-semibold">{lead.name || "—"}</span></div>
          <div className="text-sm"><span className="text-zinc-500 w-20 inline-block">Phone</span> <span className="font-mono">{lead.phone || "—"}</span></div>
          <div className="text-sm"><span className="text-zinc-500 w-20 inline-block">Email</span> <span className="font-mono">{lead.email || "—"}</span></div>
          {lead.message && <div className="text-sm text-zinc-700 mt-2 bg-zinc-50 p-3 border-l-2 border-zinc-300 whitespace-pre-line">{lead.message}</div>}
          {waLink && (
            <a href={waLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-white px-4 py-2 text-sm font-semibold mt-3" style={{ background: "#25D366" }} data-testid="admin-lead-drawer-wa">
              Open WhatsApp
            </a>
          )}
        </div>

        {/* Assignment */}
        <div className="p-5 border-b border-zinc-200">
          <div className="overline text-zinc-500 mb-2">Assign to staff</div>
          <div className="flex items-center gap-2">
            <select value={assignee} onChange={(e) => setAssignee(e.target.value)} className="flex-1 border border-zinc-300 px-3 py-2 text-sm bg-white" data-testid="admin-lead-assign-select">
              <option value="">Unassigned</option>
              {staff.map((s) => <option key={s.user_id} value={s.user_id}>{s.name || s.email}</option>)}
            </select>
            <button onClick={saveAssignee} disabled={busy} className="bg-zinc-900 hover:bg-zinc-800 text-white px-3 py-2 text-xs font-semibold" data-testid="admin-lead-assign-save">Save</button>
          </div>
        </div>

        {/* Follow-up */}
        <div className="p-5 border-b border-zinc-200">
          <div className="overline text-zinc-500 mb-2">Follow-up reminder</div>
          <div className="flex items-center gap-2">
            <input type="datetime-local" value={followUp} onChange={(e) => setFollowUp(e.target.value)} className="flex-1 border border-zinc-300 px-3 py-2 text-sm" data-testid="admin-lead-followup-input" />
            <button onClick={saveFollowUp} disabled={busy} className="bg-zinc-900 hover:bg-zinc-800 text-white px-3 py-2 text-xs font-semibold" data-testid="admin-lead-followup-save">Save</button>
          </div>
          {lead.follow_up_at && <div className="text-xs text-zinc-500 mt-2">Current: {new Date(lead.follow_up_at).toLocaleString()}</div>}
        </div>

        {/* Notes */}
        <div className="p-5 border-b border-zinc-200">
          <div className="overline text-zinc-500 mb-2">Notes ({(lead.notes || []).length})</div>
          <div className="space-y-2 mb-3 max-h-64 overflow-y-auto">
            {(lead.notes || []).slice().reverse().map((n) => (
              <div key={n.note_id} className="text-xs bg-zinc-50 p-2 border-l-2 border-zinc-300">
                <div className="text-zinc-500 mb-0.5">{n.author} · {relTime(n.created_at)}</div>
                <div className="whitespace-pre-line text-zinc-800">{n.text}</div>
              </div>
            ))}
            {(lead.notes || []).length === 0 && <div className="text-xs text-zinc-400 italic">No notes yet.</div>}
          </div>
          <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={2} placeholder="Add a note (e.g. Called at 3pm — asked for INOCHI agent brochure)…" className="w-full border border-zinc-300 px-3 py-2 text-sm" data-testid="admin-lead-note-input" />
          <button onClick={addNote} disabled={busy || !noteText.trim()} className="mt-2 bg-[#C8102E] hover:bg-[#9A0C24] disabled:opacity-50 text-white px-4 py-2 text-xs font-semibold" data-testid="admin-lead-note-save">Add note</button>
        </div>

        {/* Danger zone */}
        <div className="p-5">
          <button onClick={() => onDelete(lead.lead_id)} className="text-xs text-red-700 hover:text-red-900 border border-red-200 px-3 py-1.5 font-semibold" data-testid="admin-lead-delete">
            Delete this lead
          </button>
        </div>
      </div>
    </div>
  );
}


// ============================================================
// COMMISSIONS TAB — Growth Engine payouts (Pending/Approved/Paid/Rejected + Rules)
// ============================================================
const RM = (n) => `RM ${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function CommissionsTab({ api }) {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ counts: {} });
  const [rules, setRules] = useState([]);
  const [filter, setFilter] = useState("");
  const [ruleEditing, setRuleEditing] = useState(null);
  const [ruleForm, setRuleForm] = useState({ name: "", opportunity_slug: "", cta_kind: "", amount_type: "flat", amount: 0, currency: "RM", description: "", active: true });
  const [showRules, setShowRules] = useState(false);
  const load = async () => {
    try {
      const [a, b, c] = await Promise.all([
        api.get(`/admin/commissions${filter ? `?status=${filter}` : ""}`),
        api.get("/admin/commissions/stats"),
        api.get("/admin/commission-rules"),
      ]);
      setItems(Array.isArray(a.data) ? a.data : []);
      setStats(b.data || { counts: {} });
      setRules(Array.isArray(c.data) ? c.data : []);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  const patch = async (id, patchDoc) => {
    try {
      await api.patch(`/admin/commissions/${id}`, patchDoc);
      toast.success("Updated");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this commission?")) return;
    try { await api.delete(`/admin/commissions/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const exportCsv = () => {
    const qs = filter ? `?status=${filter}` : "";
    fetch(`${api.defaults.baseURL}/admin/commissions/export.csv${qs}`, { credentials: "include" })
      .then((r) => { if (!r.ok) throw 0; return r.blob(); })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `miresco-commissions-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
      })
      .catch(() => toast.error("CSV export failed"));
  };

  // Rules
  const openRule = (r) => {
    setRuleEditing(r?.rule_id || "new");
    setRuleForm(r ? { ...r } : { name: "", opportunity_slug: "", cta_kind: "", amount_type: "flat", amount: 0, currency: "RM", description: "", active: true });
  };
  const closeRule = () => { setRuleEditing(null); };
  const saveRule = async () => {
    try {
      if (ruleEditing === "new") await api.post("/admin/commission-rules", ruleForm);
      else await api.put(`/admin/commission-rules/${ruleEditing}`, ruleForm);
      toast.success("Rule saved"); closeRule(); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const delRule = async (id) => {
    if (!window.confirm("Delete this rule?")) return;
    try { await api.delete(`/admin/commission-rules/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-commissions-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display font-black text-2xl tracking-tight">Commissions</h2>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowRules((v) => !v)} className="text-xs uppercase tracking-widest font-semibold border border-zinc-300 hover:border-zinc-900 px-3 py-2" data-testid="admin-comm-rules-toggle">{showRules ? "Hide" : "Manage"} rules ({rules.length})</button>
          <button onClick={exportCsv} className="text-xs uppercase tracking-widest font-semibold border border-zinc-300 hover:border-zinc-900 px-3 py-2" data-testid="admin-comm-export">Export CSV</button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="admin-comm-stats">
        <div className="border border-zinc-200 p-4 bg-white"><div className="overline text-zinc-500">Pending</div><div className="font-display font-black text-2xl mt-1">{RM(stats.pending)}</div><div className="text-xs text-zinc-500 mt-1">{stats.counts?.pending || 0} entries</div></div>
        <div className="border border-zinc-200 p-4 bg-white"><div className="overline text-zinc-500">Approved</div><div className="font-display font-black text-2xl mt-1" style={{ color: "#D4AF37" }}>{RM(stats.approved)}</div><div className="text-xs text-zinc-500 mt-1">{stats.counts?.approved || 0} entries</div></div>
        <div className="border border-zinc-200 p-4 bg-white"><div className="overline text-zinc-500">Paid</div><div className="font-display font-black text-2xl mt-1" style={{ color: "#C8102E" }}>{RM(stats.paid)}</div><div className="text-xs text-zinc-500 mt-1">{stats.counts?.paid || 0} entries</div></div>
        <div className="border border-zinc-200 p-4 bg-white"><div className="overline text-zinc-500">Rejected</div><div className="font-display font-black text-2xl mt-1 text-zinc-500">{RM(stats.rejected)}</div><div className="text-xs text-zinc-500 mt-1">{stats.counts?.rejected || 0} entries</div></div>
      </div>

      {/* Rules block */}
      {showRules && (
        <div className="border border-zinc-200 bg-white p-4 space-y-3" data-testid="admin-comm-rules">
          <div className="flex items-center justify-between">
            <div className="font-display font-black">Commission Rules</div>
            <button onClick={() => openRule()} className="bg-[#C8102E] hover:bg-[#9A0C24] text-white px-3 py-1.5 text-xs font-semibold" data-testid="admin-rule-new">+ New rule</button>
          </div>
          <div className="divide-y">
            {rules.map((r) => (
              <div key={r.rule_id} className="py-2 flex items-center gap-3 text-sm">
                <div className="flex-1">
                  <div className="font-semibold">{r.name}</div>
                  <div className="text-xs text-zinc-500">{r.opportunity_slug || "(any opp)"} · {r.cta_kind || "(any CTA)"} · {r.amount_type} {r.currency} {r.amount} · {r.active ? "active" : "inactive"}</div>
                </div>
                <button onClick={() => openRule(r)} className="text-xs font-semibold border border-zinc-300 hover:border-zinc-900 px-2 py-1" data-testid={`admin-rule-edit-${r.rule_id}`}>Edit</button>
                <button onClick={() => delRule(r.rule_id)} className="text-xs font-semibold text-red-700 border border-red-200 hover:text-red-900 px-2 py-1">Delete</button>
              </div>
            ))}
            {rules.length === 0 && <div className="py-4 text-sm text-zinc-500 text-center">No rules yet.</div>}
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-2">
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="border border-zinc-300 px-3 py-2 text-sm bg-white" data-testid="admin-comm-filter">
          <option value="">All statuses</option>
          {["pending", "approved", "paid", "rejected"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-comm-table">
          <thead className="bg-zinc-50 text-left text-xs uppercase tracking-widest text-zinc-500">
            <tr><th className="p-3">Created</th><th className="p-3">User</th><th className="p-3">Opportunity / CTA</th><th className="p-3">Amount</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.commission_id} className="border-t border-zinc-200" data-testid={`admin-comm-row-${c.commission_id}`}>
                <td className="p-3 whitespace-nowrap text-zinc-500 text-xs">{c.created_at ? new Date(c.created_at).toLocaleString() : ""}</td>
                <td className="p-3 font-mono text-xs">{c.user_id}</td>
                <td className="p-3">{c.opportunity_slug || "—"} <span className="text-xs text-zinc-400">/ {c.cta_kind || "—"}</span></td>
                <td className="p-3 font-semibold">{RM(c.amount)} {c.currency}</td>
                <td className="p-3"><span className={`inline-block px-2 py-0.5 border text-[10px] uppercase tracking-widest font-semibold ${c.status === "paid" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : c.status === "approved" ? "bg-amber-50 text-amber-700 border-amber-200" : c.status === "rejected" ? "bg-zinc-100 text-zinc-500 border-zinc-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>{c.status}</span></td>
                <td className="p-3 text-right space-x-1">
                  {c.status === "pending" && <button onClick={() => patch(c.commission_id, { status: "approved" })} className="text-xs font-semibold border border-amber-300 hover:border-amber-500 px-2 py-1 bg-amber-50" data-testid={`admin-comm-approve-${c.commission_id}`}>Approve</button>}
                  {c.status === "approved" && <button onClick={() => patch(c.commission_id, { status: "paid" })} className="text-xs font-semibold text-white border border-emerald-700 bg-emerald-600 hover:bg-emerald-700 px-2 py-1" data-testid={`admin-comm-pay-${c.commission_id}`}>Mark Paid</button>}
                  {c.status !== "rejected" && c.status !== "paid" && <button onClick={() => patch(c.commission_id, { status: "rejected" })} className="text-xs font-semibold text-zinc-600 border border-zinc-300 hover:border-zinc-900 px-2 py-1">Reject</button>}
                  <button onClick={() => del(c.commission_id)} className="text-xs font-semibold text-red-700 border border-red-200 hover:text-red-900 px-2 py-1">×</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="6" className="p-10 text-center text-sm text-zinc-500">No commissions yet. When a lead is marked <b>converted</b>, an auto commission is created from the matching rule.</td></tr>}
          </tbody>
        </table>
      </div>

      {ruleEditing && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={closeRule}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md" data-testid="admin-rule-modal">
            <div className="p-5 border-b border-zinc-200 flex items-center justify-between">
              <div className="font-display font-black text-lg">{ruleEditing === "new" ? "New Rule" : "Edit Rule"}</div>
              <button onClick={closeRule}><XIcon className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <Field label="Name" value={ruleForm.name} onChange={(v) => setRuleForm({ ...ruleForm, name: v })} testid="rule-name" />
              <Field label="Opportunity slug (empty=any)" value={ruleForm.opportunity_slug} onChange={(v) => setRuleForm({ ...ruleForm, opportunity_slug: v })} placeholder="inochi" />
              <Field label="CTA kind (empty=any)" value={ruleForm.cta_kind} onChange={(v) => setRuleForm({ ...ruleForm, cta_kind: v })} placeholder="affiliate" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline text-zinc-500">Amount type</label>
                  <select value={ruleForm.amount_type} onChange={(e) => setRuleForm({ ...ruleForm, amount_type: e.target.value })} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm">
                    <option value="flat">Flat</option>
                    {/* Percent removed at launch — needs a defined deal-value base (Sprint 2E). */}
                  </select>
                </div>
                <Field label="Amount" value={ruleForm.amount} onChange={(v) => setRuleForm({ ...ruleForm, amount: Number(v) || 0 })} testid="rule-amount" />
              </div>
              <Field label="Currency" value={ruleForm.currency} onChange={(v) => setRuleForm({ ...ruleForm, currency: v })} />
              <div><label className="overline text-zinc-500">Description</label><textarea value={ruleForm.description} onChange={(e) => setRuleForm({ ...ruleForm, description: e.target.value })} rows={2} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm" /></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={ruleForm.active} onChange={(e) => setRuleForm({ ...ruleForm, active: e.target.checked })} /> Active</label>
            </div>
            <div className="p-4 border-t border-zinc-200 flex items-center justify-end gap-2">
              <button onClick={closeRule} className="px-4 py-2 text-sm font-semibold text-zinc-600 hover:text-zinc-900">Cancel</button>
              <button onClick={saveRule} className="bg-[#C8102E] hover:bg-[#9A0C24] text-white px-5 py-2 text-sm font-semibold" data-testid="admin-rule-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// BADGES TAB — Achievement CMS (admin editable rules/icons/tiers)
// ============================================================
const BADGE_EMPTY = { name: "", slug: "", icon: "Award", emoji: "🏅", tier: "bronze", description: "", rule_type: "leads_count", rule_value: 0, reward_note: "", sort_order: 0, visible: true };

function BadgesTab({ api }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BADGE_EMPTY);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const r = await api.get("/admin/badges"); setItems(Array.isArray(r.data) ? r.data : []); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  const open = (b) => { setEditing(b?.badge_id || "new"); setForm(b ? { ...b } : BADGE_EMPTY); };
  const close = () => { setEditing(null); setForm(BADGE_EMPTY); };
  const save = async () => {
    setBusy(true);
    try {
      if (editing === "new") await api.post("/admin/badges", form);
      else await api.put(`/admin/badges/${editing}`, form);
      await load(); close(); toast.success("Badge saved");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this badge?")) return;
    try { await api.delete(`/admin/badges/${id}`); await load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4" data-testid="admin-badges-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-black text-2xl tracking-tight">Badges &amp; Achievements</h2>
        <button onClick={() => open()} className="bg-[#C8102E] hover:bg-[#9A0C24] text-white px-4 py-2 text-sm font-semibold" data-testid="admin-badge-new">+ New badge</button>
      </div>
      <p className="text-sm text-zinc-500">Configure the achievement progression. Auto-badges are awarded when the rule is met on any relevant event.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map((b) => (
          <div key={b.badge_id} className="border border-zinc-200 p-4 bg-white" data-testid={`admin-badge-${b.slug}`}>
            <div className="flex items-start justify-between">
              <div className="text-3xl">{b.emoji || "🏅"}</div>
              <span className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">{b.tier}</span>
            </div>
            <div className="font-display font-black text-base tracking-tight mt-2 leading-tight">{b.name}</div>
            <div className="text-xs text-zinc-500 mt-1 line-clamp-2">{b.description || "—"}</div>
            <div className="text-[10px] uppercase tracking-widest font-semibold text-zinc-500 mt-2">Rule: {b.rule_type} ≥ {b.rule_value}</div>
            <div className="mt-3 flex items-center gap-2">
              <button onClick={() => open(b)} className="text-xs font-semibold border border-zinc-300 hover:border-zinc-900 px-2 py-1" data-testid={`admin-badge-edit-${b.badge_id}`}>Edit</button>
              <button onClick={() => del(b.badge_id)} className="text-xs font-semibold text-red-700 border border-red-200 hover:text-red-900 px-2 py-1">Delete</button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={close}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md" data-testid="admin-badge-modal">
            <div className="p-5 border-b border-zinc-200 flex items-center justify-between">
              <div className="font-display font-black text-lg">{editing === "new" ? "New Badge" : "Edit Badge"}</div>
              <button onClick={close}><XIcon className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="badge-name" />
              <Field label="Slug (unique)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v.toLowerCase().replace(/\s+/g, "-") })} testid="badge-slug" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Emoji" value={form.emoji} onChange={(v) => setForm({ ...form, emoji: v })} />
                <Field label="Icon (Lucide)" value={form.icon} onChange={(v) => setForm({ ...form, icon: v })} />
              </div>
              <div>
                <label className="overline text-zinc-500">Tier</label>
                <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm">
                  <option>bronze</option><option>silver</option><option>gold</option><option>diamond</option><option>platinum</option>
                </select>
              </div>
              <div><label className="overline text-zinc-500">Description</label><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline text-zinc-500">Rule type</label>
                  <select value={form.rule_type} onChange={(e) => setForm({ ...form, rule_type: e.target.value })} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm">
                    <option value="leads_count">Leads count</option>
                    <option value="referral_signups">Referral signups</option>
                    <option value="commission_earned_myr">Commission earned (RM)</option>
                    <option value="manual">Manual (admin awards only)</option>
                  </select>
                </div>
                <Field label="Rule value (goal)" value={form.rule_value} onChange={(v) => setForm({ ...form, rule_value: Number(v) || 0 })} testid="badge-rule-value" />
              </div>
              <Field label="Reward note" value={form.reward_note} onChange={(v) => setForm({ ...form, reward_note: v })} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Sort order" value={form.sort_order} onChange={(v) => setForm({ ...form, sort_order: Number(v) || 0 })} />
                <label className="flex items-center gap-2 text-sm mt-6"><input type="checkbox" checked={form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })} /> Visible</label>
              </div>
            </div>
            <div className="p-4 border-t border-zinc-200 flex items-center justify-end gap-2">
              <button onClick={close} className="px-4 py-2 text-sm font-semibold text-zinc-600 hover:text-zinc-900">Cancel</button>
              <button onClick={save} disabled={busy} className="bg-[#C8102E] hover:bg-[#9A0C24] disabled:opacity-50 text-white px-5 py-2 text-sm font-semibold" data-testid="admin-badge-save">{busy ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ============================================================
// SPRINT 2D — Success Stories · Ad Packages · Gateways
// ============================================================
const STORY_EMPTY = { slug: "", title: "", story_type: "entrepreneur", hero_image_url: "", video_url: "", quote: "", article: "", name: "", role: "", location: "", category_slug: "", opportunity_slug: "", monthly_income_myr: 0, tags: [], priority: 0, featured: false, published: true };

function SuccessStoriesTab({ api }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(STORY_EMPTY);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const r = await api.get("/admin/success-stories"); setItems(Array.isArray(r.data) ? r.data : []); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  const open = (s) => { setEditing(s?.story_id || "new"); setForm(s ? { ...STORY_EMPTY, ...s } : STORY_EMPTY); };
  const close = () => { setEditing(null); setForm(STORY_EMPTY); };
  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, monthly_income_myr: Number(form.monthly_income_myr) || 0 };
      if (typeof payload.tags === "string") payload.tags = payload.tags.split(",").map((s) => s.trim()).filter(Boolean);
      if (editing === "new") await api.post("/admin/success-stories", payload);
      else await api.put(`/admin/success-stories/${editing}`, payload);
      await load(); close(); toast.success("Story saved");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this story?")) return;
    try { await api.delete(`/admin/success-stories/${id}`); await load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4" data-testid="admin-stories-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-black text-2xl tracking-tight">Success Stories</h2>
        <button onClick={() => open()} data-testid="admin-story-new" className="bg-[#C8102E] hover:bg-[#9A0C24] text-white px-4 py-2 text-sm font-semibold">+ New story</button>
      </div>
      <p className="text-sm text-zinc-500">Trust-builders. Live at <code>/success-stories</code>.</p>
      <div className="border border-zinc-200 divide-y">
        {items.map((s) => (
          <div key={s.story_id} className="p-4 flex items-center gap-4">
            <div className="w-14 h-14 bg-zinc-100 border border-zinc-200 shrink-0 overflow-hidden">
              {s.hero_image_url ? <img src={s.hero_image_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-xs text-zinc-400">no img</div>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate">{s.title}</div>
              <div className="text-xs text-zinc-500">{s.name} · {s.story_type} · RM {s.monthly_income_myr}/mo · {s.published ? "published" : "draft"}{s.featured ? " · ⭐" : ""}</div>
            </div>
            <button onClick={() => open(s)} className="text-xs font-semibold border border-zinc-300 hover:border-zinc-900 px-2 py-1" data-testid={`admin-story-edit-${s.story_id}`}>Edit</button>
            <button onClick={() => del(s.story_id)} className="text-xs font-semibold text-red-700 border border-red-200 hover:text-red-900 px-2 py-1">×</button>
          </div>
        ))}
        {items.length === 0 && <div className="p-8 text-center text-sm text-zinc-500">No stories yet.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={close}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="admin-story-modal">
            <div className="p-5 border-b border-zinc-200 sticky top-0 bg-white flex items-center justify-between">
              <div className="font-display font-black text-lg">{editing === "new" ? "New Story" : "Edit Story"}</div>
              <button onClick={close}><XIcon className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <Field label="Slug" value={form.slug} onChange={(v) => setForm({ ...form, slug: v.toLowerCase().replace(/\s+/g, "-") })} testid="story-slug" />
              <Field label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="story-title" />
              <div className="grid grid-cols-2 gap-3">
                <div><label className="overline text-zinc-500">Type</label>
                  <select value={form.story_type} onChange={(e) => setForm({ ...form, story_type: e.target.value })} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm">
                    {["entrepreneur","affiliate","agent","stockist","business_owner","community"].map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <Field label="Monthly income (RM)" value={form.monthly_income_myr} onChange={(v) => setForm({ ...form, monthly_income_myr: Number(v) || 0 })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Hero image URL" value={form.hero_image_url} onChange={(v) => setForm({ ...form, hero_image_url: v })} />
                <Field label="Video URL" value={form.video_url} onChange={(v) => setForm({ ...form, video_url: v })} />
              </div>
              <div><label className="overline text-zinc-500">Quote</label><textarea value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} rows={2} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm" /></div>
              <div><label className="overline text-zinc-500">Article</label><textarea value={form.article} onChange={(e) => setForm({ ...form, article: e.target.value })} rows={5} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm" /></div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                <Field label="Role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} />
                <Field label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Category slug" value={form.category_slug} onChange={(v) => setForm({ ...form, category_slug: v })} />
                <Field label="Opportunity slug" value={form.opportunity_slug} onChange={(v) => setForm({ ...form, opportunity_slug: v })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Priority" value={form.priority} onChange={(v) => setForm({ ...form, priority: Number(v) || 0 })} />
                <div className="flex items-center gap-4 mt-6">
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured</label>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published</label>
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-zinc-200 sticky bottom-0 bg-white flex items-center justify-end gap-2">
              <button onClick={close} className="px-4 py-2 text-sm font-semibold text-zinc-600 hover:text-zinc-900">Cancel</button>
              <button onClick={save} disabled={busy} className="bg-[#C8102E] hover:bg-[#9A0C24] disabled:opacity-50 text-white px-5 py-2 text-sm font-semibold" data-testid="admin-story-save">{busy ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const AD_EMPTY = { name: "", slot: "featured_opportunity", price: 0, currency: "RM", duration_days: 30, display_position: "", description: "", features: [], wa_template: "", sort_order: 0, active: true };

function AdPackagesTab({ api }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(AD_EMPTY);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const r = await api.get("/admin/ad-packages"); setItems(Array.isArray(r.data) ? r.data : []); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  const open = (p) => { setEditing(p?.package_id || "new"); setForm(p ? { ...AD_EMPTY, ...p } : AD_EMPTY); };
  const close = () => { setEditing(null); setForm(AD_EMPTY); };
  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, price: Number(form.price) || 0, duration_days: Number(form.duration_days) || 0, sort_order: Number(form.sort_order) || 0 };
      if (typeof payload.features === "string") payload.features = payload.features.split("\n").map((s) => s.trim()).filter(Boolean);
      if (editing === "new") await api.post("/admin/ad-packages", payload);
      else await api.put(`/admin/ad-packages/${editing}`, payload);
      await load(); close(); toast.success("Package saved");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this package?")) return;
    try { await api.delete(`/admin/ad-packages/${id}`); await load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4" data-testid="admin-ads-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-black text-2xl tracking-tight">Ad Packages</h2>
        <button onClick={() => open()} data-testid="admin-ad-new" className="bg-[#C8102E] hover:bg-[#9A0C24] text-white px-4 py-2 text-sm font-semibold">+ New package</button>
      </div>
      <p className="text-sm text-zinc-500">Featured Opportunity · Homepage Banner · Featured Video · Top Search · Category / Territory Sponsor.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((p) => (
          <div key={p.package_id} className="border border-zinc-200 p-4 bg-white" data-testid={`admin-ad-${p.package_id}`}>
            <div className="text-[10px] uppercase tracking-widest font-semibold text-zinc-500">{p.slot}</div>
            <div className="font-display font-black text-lg tracking-tight mt-1">{p.name}</div>
            <div className="text-xs text-zinc-500 mt-1">{p.duration_days} days · {p.display_position}</div>
            <div className="mt-2 font-display font-black text-2xl" style={{ color: "#C8102E" }}>{p.currency} {p.price}</div>
            <div className="mt-3 flex items-center gap-2">
              <button onClick={() => open(p)} className="text-xs font-semibold border border-zinc-300 hover:border-zinc-900 px-2 py-1" data-testid={`admin-ad-edit-${p.package_id}`}>Edit</button>
              <button onClick={() => del(p.package_id)} className="text-xs font-semibold text-red-700 border border-red-200 hover:text-red-900 px-2 py-1">Delete</button>
              <span className={`text-[10px] uppercase tracking-widest font-bold ml-auto ${p.active ? "text-emerald-700" : "text-zinc-400"}`}>{p.active ? "active" : "inactive"}</span>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={close}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-lg max-h-[92vh] overflow-y-auto" data-testid="admin-ad-modal">
            <div className="p-5 border-b border-zinc-200 flex items-center justify-between">
              <div className="font-display font-black text-lg">{editing === "new" ? "New Ad Package" : "Edit Ad Package"}</div>
              <button onClick={close}><XIcon className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="ad-name" />
              <div><label className="overline text-zinc-500">Slot</label>
                <select value={form.slot} onChange={(e) => setForm({ ...form, slot: e.target.value })} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm">
                  {["featured_opportunity","homepage_banner","featured_video","top_search","category_sponsor","territory_sponsor"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Price" value={form.price} onChange={(v) => setForm({ ...form, price: Number(v) || 0 })} testid="ad-price" />
                <Field label="Duration (days)" value={form.duration_days} onChange={(v) => setForm({ ...form, duration_days: Number(v) || 0 })} />
              </div>
              <Field label="Currency" value={form.currency} onChange={(v) => setForm({ ...form, currency: v })} />
              <Field label="Display position" value={form.display_position} onChange={(v) => setForm({ ...form, display_position: v })} />
              <div><label className="overline text-zinc-500">Description</label><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm" /></div>
              <div><label className="overline text-zinc-500">Features (one per line)</label><textarea value={Array.isArray(form.features) ? form.features.join("\n") : form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} rows={4} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm font-mono" /></div>
              <div><label className="overline text-zinc-500">WhatsApp template</label><textarea value={form.wa_template} onChange={(e) => setForm({ ...form, wa_template: e.target.value })} rows={2} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm" /></div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Sort order" value={form.sort_order} onChange={(v) => setForm({ ...form, sort_order: Number(v) || 0 })} />
                <label className="flex items-center gap-2 text-sm mt-6"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
              </div>
            </div>
            <div className="p-4 border-t border-zinc-200 flex items-center justify-end gap-2">
              <button onClick={close} className="px-4 py-2 text-sm font-semibold text-zinc-600 hover:text-zinc-900">Cancel</button>
              <button onClick={save} disabled={busy} className="bg-[#C8102E] hover:bg-[#9A0C24] disabled:opacity-50 text-white px-5 py-2 text-sm font-semibold" data-testid="admin-ad-save">{busy ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GatewaysTab({ api }) {
  const [cfg, setCfg] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/gateway-config").then((r) => setCfg(r.data || {})).catch(() => {}); }, []);
  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/gateway-config", cfg); toast.success("Gateway config saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const rowCls = "border border-zinc-200 p-4 bg-white space-y-3";
  return (
    <div className="space-y-4" data-testid="admin-gateways-tab">
      <h2 className="font-display font-black text-2xl tracking-tight">Payment Gateways</h2>
      <p className="text-sm text-zinc-500">Toggle live gateways and store API credentials. Live activation happens in Sprint 2E.</p>
      <div className={rowCls}>
        <label className="flex items-center gap-2 font-semibold"><input type="checkbox" checked={!!cfg.billplz_enabled} onChange={(e) => setCfg({ ...cfg, billplz_enabled: e.target.checked })} data-testid="gw-billplz-toggle" /> Billplz</label>
        <Field label="Billplz API key" value={cfg.billplz_api_key || ""} onChange={(v) => setCfg({ ...cfg, billplz_api_key: v })} testid="gw-billplz-key" />
        <Field label="Billplz collection ID" value={cfg.billplz_collection_id || ""} onChange={(v) => setCfg({ ...cfg, billplz_collection_id: v })} />
      </div>
      <div className={rowCls}>
        <label className="flex items-center gap-2 font-semibold"><input type="checkbox" checked={!!cfg.toyyibpay_enabled} onChange={(e) => setCfg({ ...cfg, toyyibpay_enabled: e.target.checked })} data-testid="gw-toyyibpay-toggle" /> ToyyibPay</label>
        <Field label="ToyyibPay secret key" value={cfg.toyyibpay_secret_key || ""} onChange={(v) => setCfg({ ...cfg, toyyibpay_secret_key: v })} testid="gw-toyyibpay-key" />
        <Field label="ToyyibPay category code" value={cfg.toyyibpay_category_code || ""} onChange={(v) => setCfg({ ...cfg, toyyibpay_category_code: v })} />
      </div>
      <div className={rowCls}>
        <label className="flex items-center gap-2 font-semibold"><input type="checkbox" checked={!!cfg.stripe_enabled} onChange={(e) => setCfg({ ...cfg, stripe_enabled: e.target.checked })} data-testid="gw-stripe-toggle" /> Stripe</label>
        <Field label="Stripe secret key" value={cfg.stripe_secret_key || ""} onChange={(v) => setCfg({ ...cfg, stripe_secret_key: v })} testid="gw-stripe-key" />
        <Field label="Stripe publishable key" value={cfg.stripe_publishable_key || ""} onChange={(v) => setCfg({ ...cfg, stripe_publishable_key: v })} />
      </div>
      <div className={rowCls}>
        <label className="flex items-center gap-2 font-semibold"><input type="checkbox" checked={cfg.manual_bank_enabled !== false} onChange={(e) => setCfg({ ...cfg, manual_bank_enabled: e.target.checked })} data-testid="gw-manual-toggle" /> Manual Bank Transfer</label>
        <p className="text-xs text-zinc-500">Bank details are managed in the Settings tab.</p>
      </div>
      <button onClick={save} disabled={busy} className="bg-[#C8102E] hover:bg-[#9A0C24] disabled:opacity-50 text-white px-5 py-2.5 font-semibold text-sm" data-testid="gw-save">{busy ? "Saving…" : "Save gateway config"}</button>
    </div>
  );
}


// ============================================================
// JOURNEYS TAB — Guided Growth CMS (Sprint 2E)
// ============================================================
const JOURNEY_EMPTY = { slug: "", title: "", subtitle: "", hero_image_url: "", track: "earn", target_opportunity_slug: "", target_role: "", steps: [], estimated_days: 7, reward_badges: [], reward_note: "", featured: false, sort_order: 0, published: true };

function JourneysTab({ api }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(JOURNEY_EMPTY);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const r = await api.get("/admin/journeys"); setItems(Array.isArray(r.data) ? r.data : []); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  const open = (j) => { setEditing(j?.journey_id || "new"); setForm(j ? { ...JOURNEY_EMPTY, ...j } : JOURNEY_EMPTY); };
  const close = () => { setEditing(null); setForm(JOURNEY_EMPTY); };
  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, estimated_days: Number(form.estimated_days) || 7, sort_order: Number(form.sort_order) || 0 };
      if (typeof payload.reward_badges === "string") payload.reward_badges = payload.reward_badges.split(",").map((s) => s.trim()).filter(Boolean);
      if (typeof payload.steps === "string") { try { payload.steps = JSON.parse(payload.steps); } catch { toast.error("Steps must be valid JSON array"); setBusy(false); return; } }
      if (editing === "new") await api.post("/admin/journeys", payload);
      else await api.put(`/admin/journeys/${editing}`, payload);
      await load(); close(); toast.success("Journey saved");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this journey?")) return;
    try { await api.delete(`/admin/journeys/${id}`); await load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4" data-testid="admin-journeys-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-black text-2xl tracking-tight">Guided Journeys</h2>
        <button onClick={() => open()} className="bg-[#C8102E] hover:bg-[#9A0C24] text-white px-4 py-2 text-sm font-semibold" data-testid="admin-journey-new">+ New journey</button>
      </div>
      <p className="text-sm text-zinc-500">3 tracks: <b>Earn</b> · <b>Grow</b> · <b>Creator</b>. Steps are edited as JSON below. Each step: title, subtitle, kind (video/read/download/cta_wa/action/external/checkbox), body, wa_template, external_url, download_url, required, reward_note.</p>
      <div className="border border-zinc-200 divide-y">
        {items.map((j) => (
          <div key={j.journey_id} className="p-4 flex items-center gap-4">
            <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border border-zinc-300">{j.track}</span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate">{j.title}</div>
              <div className="text-xs text-zinc-500 truncate">/{j.slug} · {(j.steps || []).length} steps · {j.estimated_days}d · {j.published ? "published" : "draft"}{j.featured ? " · ⭐" : ""}</div>
            </div>
            <a href={`/journey/${j.slug}`} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-zinc-500 hover:text-[#C8102E] underline">View</a>
            <button onClick={() => open(j)} className="text-xs font-semibold border border-zinc-300 hover:border-zinc-900 px-2 py-1" data-testid={`admin-journey-edit-${j.journey_id}`}>Edit</button>
            <button onClick={() => del(j.journey_id)} className="text-xs font-semibold text-red-700 border border-red-200 hover:text-red-900 px-2 py-1">×</button>
          </div>
        ))}
        {items.length === 0 && <div className="p-8 text-center text-sm text-zinc-500">No journeys yet.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={close}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="admin-journey-modal">
            <div className="p-5 border-b border-zinc-200 sticky top-0 bg-white flex items-center justify-between">
              <div className="font-display font-black text-lg">{editing === "new" ? "New Journey" : "Edit Journey"}</div>
              <button onClick={close}><XIcon className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <Field label="Slug" value={form.slug} onChange={(v) => setForm({ ...form, slug: v.toLowerCase().replace(/\s+/g, "-") })} testid="journey-slug" />
              <Field label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="journey-title" />
              <Field label="Subtitle" value={form.subtitle} onChange={(v) => setForm({ ...form, subtitle: v })} />
              <div className="grid grid-cols-2 gap-3">
                <div><label className="overline text-zinc-500">Track</label>
                  <select value={form.track} onChange={(e) => setForm({ ...form, track: e.target.value })} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm">
                    <option value="earn">Earn</option><option value="grow">Grow</option><option value="creator">Creator</option>
                  </select>
                </div>
                <Field label="Estimated days" value={form.estimated_days} onChange={(v) => setForm({ ...form, estimated_days: Number(v) || 7 })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Target opportunity slug" value={form.target_opportunity_slug} onChange={(v) => setForm({ ...form, target_opportunity_slug: v })} />
                <Field label="Target role" value={form.target_role} onChange={(v) => setForm({ ...form, target_role: v })} placeholder="entrepreneur / business_owner / influencer" />
              </div>
              <Field label="Hero image URL" value={form.hero_image_url} onChange={(v) => setForm({ ...form, hero_image_url: v })} />
              <Field label="Reward badges (comma-separated slugs)" value={Array.isArray(form.reward_badges) ? form.reward_badges.join(", ") : form.reward_badges} onChange={(v) => setForm({ ...form, reward_badges: v })} placeholder="explorer, starter-entrepreneur" />
              <Field label="Reward note (visible summary)" value={form.reward_note} onChange={(v) => setForm({ ...form, reward_note: v })} />
              <div>
                <label className="overline text-zinc-500">Steps (JSON array)</label>
                <textarea
                  value={typeof form.steps === "string" ? form.steps : JSON.stringify(form.steps || [], null, 2)}
                  onChange={(e) => { try { setForm({ ...form, steps: JSON.parse(e.target.value) }); } catch { setForm({ ...form, steps: e.target.value }); } }}
                  rows={12}
                  className="mt-2 w-full border border-zinc-300 px-3 py-2 text-xs font-mono"
                  data-testid="journey-steps"
                />
                <div className="text-[10px] text-zinc-500 mt-1">Each step needs: title, kind, subtitle, body, wa_template, external_url, order, required.</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Sort order" value={form.sort_order} onChange={(v) => setForm({ ...form, sort_order: Number(v) || 0 })} />
                <div className="flex items-center gap-4 mt-6">
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured</label>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published</label>
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-zinc-200 sticky bottom-0 bg-white flex items-center justify-end gap-2">
              <button onClick={close} className="px-4 py-2 text-sm font-semibold text-zinc-600 hover:text-zinc-900">Cancel</button>
              <button onClick={save} disabled={busy} className="bg-[#C8102E] hover:bg-[#9A0C24] disabled:opacity-50 text-white px-5 py-2 text-sm font-semibold" data-testid="admin-journey-save">{busy ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

